<script lang="ts">
export const ALL_ACTIVITIES_WORKSPACE_ID = '__all_activities__'
</script>

<script setup lang="ts">
import {
  ArrowUpDown as ArrowUpDownIcon,
  ArrowUpRight as ArrowUpRightIcon,
  Bike as BikeIcon,
  CircleSlash2 as CircleSlash2Icon,
  Clock as ClockIcon,
  Check as CheckIcon,
  Bookmark as BookmarkIcon,
  ChevronDown as ChevronDownIcon,
  ChevronLeft as ChevronLeftIcon,
  Download as DownloadIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  GalleryVerticalEnd as GalleryVerticalEndIcon,
  Globe as GlobeIcon,
  Image as ImageIcon,
  Library as LibraryIcon,
  MapPin as MapPinIcon,
  Users as UsersIcon,
  Info as InfoIcon,
  MoreHorizontal as MoreHorizontalIcon,
  LogOut as LogOutIcon,
  Maximize as MaximizeIcon,
  PanelTopClose as PanelTopCloseIcon,
  Pencil as PencilIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  ScrollText as ScrollTextIcon,
  Plus as PlusIcon,
  Route as RouteIcon,
  Ruler as RulerIcon,
  Share2 as ShareIcon,
  Trash2 as TrashIcon,
  Undo2 as UndoIcon,
  Upload as UploadIcon,
  WifiOff as WifiOffIcon,
  X as XIcon,
} from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import CreateCollectionIcon from '@/components/icons/CreateCollectionIcon.vue'
import CreateTourModal from '@/components/map/modals/CreateTourModal.vue'
import GpxDownloadOptionsModal from '@/components/map/modals/GpxDownloadOptionsModal.vue'
import ShareModal from '@/components/map/modals/ShareWorkspaceModal.vue'
import BaseIconButton from '@/components/ui/BaseIconButton.vue'
import BaseSectionHeader from '@/components/ui/BaseSectionHeader.vue'
import BaseSegmentedControl, {
  type SegmentedOption,
} from '@/components/ui/BaseSegmentedControl.vue'
import BaseToggle from '@/components/ui/BaseToggle.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useSyncToMap } from '@/composables/useSyncToMap'
import { apiService } from '@/services/apiService'
import {
  getLastCollectionRouteTarget,
  rememberLastCollectionContext,
} from '@/services/collectionContextService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import {
  formatCollectionDistance,
  formatCollectionDuration,
  exactCollectionDuration,
  getDaysUntilDeletion,
} from '@/services/formattingService'
import { parseLineString, calculateBounds } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { requireOnlineModificationAccess } from '@/services/offlineModificationGuardService'
import { hasPermission, requirePermission } from '@/services/permissionService'
import {
  canUnpinPOIFromPinned,
  setPinnedItemsBatch,
  unpinAllPinnedItems,
} from '@/services/pinnedService'
import { isTauri } from '@/services/platformService'
import { requestRouteModeLeave } from '@/services/routeModeLeaveGuardService'
import { settingsService } from '@/services/settingsService'
import { showError, showSuccess } from '@/services/toastService'
import { shouldHideAscentForTrack } from '@/services/tourTypeService'
import {
  calculateHaversineDistance,
  calculateTrackDistance,
  getTrackElevationGain,
} from '@/services/trackMetricsCalculationService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useOfflineStore } from '@/stores/offlineStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { getBestTrackLineString } from '@/utils/trackGeometry'

import ActivityRouteCollection from './toolbar/ActivityRouteCollection.vue'
import CollectionRouteEmptyState from './toolbar/CollectionRouteEmptyState.vue'
import PhotoList from './toolbar/PhotoList.vue'
import PinnedEmptyState from './toolbar/PinnedEmptyState.vue'
import TourList from './toolbar/TourList.vue'
import WorkspacePOIList from './toolbar/WorkspacePOIList.vue'
import WorkspaceSearchPanel from './toolbar/WorkspaceSearchPanel.vue'

defineOptions({
  name: 'WorkspaceToolbar',
})

const emit = defineEmits<{
  'trigger-gpx-upload': []
  'trigger-photo-upload': []
  'tour-click': [tourId: string]
  'view-photo': [photoId: string]
  'zoom-photo': [photoId: string]
  'zoom-poi': [poiId: string]
  'open-poi-detail': [poiId: string]
  'zoom-tour': [tourId: string]
  'zoom-waypoint': [tourId: string, waypointIndex: number]
  'select-tour': [tourId: string]
  'select-workspace': [workspaceId: string]
  'make-offline': [workspaceId: string]
  'create-tour': []
  'enter-track-edit': [tourId: string, trackClientId: string]
  'create-new-workspace': []
}>()

defineProps<{
  selectedPoiId?: string | null
}>()

const workspaceStore = useWorkspaceStore()
const authStore = useAuthStore()
const photoStore = usePhotoStore()
const libraryStore = useLibraryStore()
const offlineStore = useOfflineStore()
const isTauriApp = isTauri()
const { photosSortedByDate } = storeToRefs(photoStore)
const hasPhotos = computed(() => (photosSortedByDate?.value?.length ?? 0) > 0)
const tours = computed(() => workspaceStore.tours)
const hasTours = computed(() => tours.value.length > 0)
const hasPOIs = computed(() => workspaceStore.allWorkspacePOIs.length > 0)
const isPinnedWorkspaceEmpty = computed(() => !hasTours.value && !hasPOIs.value && !hasPhotos.value)
const hasItemsToUnpin = computed(
  () =>
    hasTours.value ||
    workspaceStore.allWorkspacePOIs.some((poi) =>
      canUnpinPOIFromPinned(poi, workspaceStore.clientId),
    ),
)
const canUnpinAllPinnedItems = computed(() => hasItemsToUnpin.value && hasPermission('edit_tour'))
const currentWorkspacePinnableItems = computed(() => [
  ...workspaceStore.tours,
  ...workspaceStore.allWorkspacePOIs,
])
const hasCurrentWorkspacePinnableItems = computed(
  () => currentWorkspacePinnableItems.value.length > 0,
)
const areAllCurrentWorkspaceItemsPinned = computed(
  () =>
    hasCurrentWorkspacePinnableItems.value &&
    currentWorkspacePinnableItems.value.every((item) => item.isPinned === true),
)
const isCurrentWorkspaceOffline = computed(() => offlineStore.isOffline(workspaceStore.clientId))
const visibleCollectionElevationGain = computed(() => {
  let totalGain: number | null = null
  for (const tour of workspaceStore.tours) {
    for (const track of tour.tracks) {
      if (shouldHideAscentForTrack(track, tour.type)) continue
      const gain = getTrackElevationGain(track)
      if (gain && gain > 0) totalGain = (totalGain ?? 0) + gain
    }
  }
  return totalGain
})
const isAnyTourVisible = computed(() => tours.value.some((tour) => tour.visible))
const uiStore = useUiStore()
const isCreateTourModalOpen = ref(false)
const isWorkspaceGpxDownloadModalOpen = ref(false)
const isWorkspaceGpxDownloading = ref(false)
const searchWorkspaceStore = useSearchWorkspaceStore()
const router = useRouter()
const route = useRoute()

const handleZoomToCollections = () => {
  const bounds = searchWorkspaceStore.getVisibleWorkspacesBounds()
  if (bounds) {
    uiStore.beginMapNavigation('explicit')
    window.dispatchEvent(
      new CustomEvent('zoom-to-search-workspaces', { detail: { bounds, force: true } }),
    )
  }
}

const centerDetailWorkspaceOnMap = () => {
  const coordinateArrays: [number, number][][] = []
  for (const tour of workspaceStore.allTours) {
    for (const track of tour.tracks) {
      if (track.pointsLineReduced500m) {
        const coords = parseLineString(track.pointsLineReduced500m)
        if (coords.length > 0) coordinateArrays.push(coords)
      }
    }
  }
  const bounds = calculateBounds(coordinateArrays)
  if (!bounds) return
  closeCoverMenu()
  uiStore.beginMapNavigation('explicit')
  window.dispatchEvent(
    new CustomEvent('zoom-to-search-workspaces', { detail: { bounds, force: true } }),
  )
}

// Scroll container for collection detail navigation.
const scrollContainer = ref<HTMLElement | null>(null)
const toursSectionAnchorRef = ref<HTMLElement | null>(null)
// BaseSectionHeader exposes its root element via `rootEl`. Store the component instance and read
// `.rootEl` lazily at measurement time — capturing `inst.rootEl` inside the ref callback grabs it
// before the child's root element is assigned, so it would stay null forever (and the first null
// header makes updateSectionHints reset every hint).
type SectionHeaderInstance = { rootEl?: HTMLElement | null } | null
const toursHeaderInstance = ref<SectionHeaderInstance>(null)
const poiHeaderInstance = ref<SectionHeaderInstance>(null)
const setToursHeaderEl = (inst: unknown) =>
  (toursHeaderInstance.value = (inst as SectionHeaderInstance) ?? null)
const setPoiHeaderEl = (inst: unknown) =>
  (poiHeaderInstance.value = (inst as SectionHeaderInstance) ?? null)
const photosHeaderRef = ref<HTMLElement | null>(null)
const poiSectionAnchorRef = ref<HTMLElement | null>(null)
const photosSectionAnchorRef = ref<HTMLElement | null>(null)
const showToursTopHint = ref(false)
const showPOITopHint = ref(false)
const showPhotosTopHint = ref(false)
const showToursBottomHint = ref(false)
const showPOIBottomHint = ref(false)
const showPhotosBottomHint = ref(false)
type SectionScrollTarget = 'tours' | 'poi' | 'photos'
const pendingSectionScroll = ref<SectionScrollTarget | null>(null)

// Use store properties directly - storeToRefs is not needed for simple reactive access
const isLoadingPhotos = computed(() => photoStore?.isLoading ?? false)
const arePhotosVisible = computed(() => uiStore.arePhotosVisible)
const isPublic = computed(() => workspaceStore?.isPublic ?? false)
const isWorkspaceMember = computed(
  () => workspaceStore.isOwner || workspaceStore.isCollaborator || workspaceStore.isViewer,
)

// Navigation state - use route params as the source of truth (workspaceStore.clientId is always
// truthy on init due to generateId(), so it cannot distinguish "no workspace" from "workspace loaded")
const isPinnedRoute = () => route.name === 'pinned'
const hasActiveWorkspace =
  (typeof route.params.workspaceId === 'string' && !!route.params.workspaceId) || isPinnedRoute()

// Delayed loading indicator (bike) for collection detail view
const routeWorkspaceId = computed(() =>
  typeof route.params.workspaceId === 'string' ? (route.params.workspaceId as string) : null,
)
const isDetailLoading = computed(
  () => routeWorkspaceId.value !== null && workspaceStore.clientId !== routeWorkspaceId.value,
)
const showLoadingIcon = ref(false)
let loadingIconTimer: number | null = null
// Delayed loading text (200ms) to avoid flicker
const showLoadingText = ref(false)
let loadingTextTimer: number | null = null

// Displayed name hides default during loading and shows after 200ms
const displayedWorkspaceName = computed(() =>
  isDetailLoading.value ? (showLoadingText.value ? 'Loading...' : '') : selectedWorkspaceName.value,
)

const coverAuthorByline = computed((): string | null => {
  const ownerNames = workspaceStore.ownerNames
  if (!ownerNames || ownerNames.length === 0) {
    return null
  }

  const currentUserName = authStore.user?.name?.trim().toLocaleLowerCase()
  if (!currentUserName) {
    return ownerNames.join(', ')
  }

  const isCurrentUserListed = ownerNames.some(
    (ownerName) => ownerName.trim().toLocaleLowerCase() === currentUserName,
  )

  return isCurrentUserListed ? null : ownerNames.join(', ')
})

watch(isDetailLoading, (loading) => {
  if (loading) {
    if (loadingIconTimer) clearTimeout(loadingIconTimer)
    loadingIconTimer = window.setTimeout(() => {
      if (isDetailLoading.value) showLoadingIcon.value = true
    }, 800)

    if (loadingTextTimer) clearTimeout(loadingTextTimer)
    loadingTextTimer = window.setTimeout(() => {
      if (isDetailLoading.value) showLoadingText.value = true
    }, 200)
  } else {
    if (loadingIconTimer) {
      clearTimeout(loadingIconTimer)
      loadingIconTimer = null
    }
    showLoadingIcon.value = false

    if (loadingTextTimer) {
      clearTimeout(loadingTextTimer)
      loadingTextTimer = null
    }
    showLoadingText.value = false
  }
})
const currentView = ref<'workspaces' | 'workspace-detail' | 'all-activities'>(
  hasActiveWorkspace ? 'workspace-detail' : 'workspaces',
)
const selectedWorkspaceName = ref<string>(
  hasActiveWorkspace ? workspaceStore.name || 'Untitled Collection' : '',
)

type DesktopSurface = 'pinned' | 'collections' | 'library'

const desktopSurfaceOptions: SegmentedOption[] = [
  { value: 'pinned', label: 'Pinned', icon: PinIcon, testId: 'desktop-surface-pinned' },
  {
    value: 'collections',
    label: 'Collections',
    icon: GalleryVerticalEndIcon,
    testId: 'desktop-surface-collections',
  },
  { value: 'library', label: 'Library', icon: LibraryIcon, testId: 'desktop-surface-library' },
]

// If a collection is already active on initial load, show the workspace detail panel automatically
// (removed: panel no longer opens automatically)

// Share modal state
const isShareModalOpen = ref(false)
const isBookmarkBusy = ref(false)

// Keep global UI dialog state in sync so header hides during dialogs
watch(
  () => isShareModalOpen.value,
  (open) => {
    if (open) uiStore.setDialogOpen(true)
  },
)

const isLoadingTours = computed(() => workspaceStore._isLoading || false)
const isContentLoading = computed(() => isLoadingTours.value || isLoadingPhotos.value)

// Description editing
const isEditingDescription = ref(false)
const descriptionExpanded = ref(false)
const editedDescription = ref('')
const descriptionError = ref('')
const descriptionTextareaRef = ref<HTMLTextAreaElement | null>(null)

const startEditingDescription = async () => {
  if (!(await requirePermission('edit_workspace_description'))) return
  editedDescription.value = workspaceStore.description || ''
  descriptionError.value = ''
  descriptionExpanded.value = true
  isEditingDescription.value = true
  nextTick(() => {
    const el = descriptionTextareaRef.value
    if (el) {
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
      el.focus()
    }
  })
}

const saveDescription = async () => {
  if (!isEditingDescription.value) return
  if (editedDescription.value.length > 2048) {
    descriptionError.value = 'Description must be 2048 characters or fewer'
    return
  }
  if (!(await requirePermission('edit_workspace_description'))) return
  workspaceStore.updateWorkspace({ description: editedDescription.value })
  isEditingDescription.value = false
  descriptionError.value = ''
}

const cancelEditingDescription = () => {
  isEditingDescription.value = false
  descriptionError.value = ''
}

// Linkify description: escape HTML then wrap http(s) URLs in <a> tags.
// HTML escaping must happen first to prevent injection via user-supplied text.
const linkifyDescription = computed((): string | null => {
  const raw = workspaceStore.description
  if (!raw) return null
  const escaped = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
  return escaped.replace(
    /https?:\/\/[^\s<>"']+(?<![.,;:!?)])/g,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-emerald-600 underline hover:text-emerald-700 break-all">${url}</a>`,
  )
})

// Check if workspace is saved and can be shared
const isSavedWorkspace = computed(
  () => workspaceStore.currentWorkspace?._createEventPushed === true,
)
const isSystemWorkspace = computed(() => workspaceStore.isSystem === true)
const canAddCollectionContent = computed(() => hasPermission('edit_track'))
const canAddPOIToCollection = computed(() => hasPermission('edit_waypoint'))
const canEditCollectionMetadata = computed(
  () => !isSystemWorkspace.value && hasPermission('rename_workspace'),
)
const canEditCollectionDescription = computed(
  () => !isSystemWorkspace.value && hasPermission('edit_workspace_description'),
)
const canDeletePhotos = computed(() => hasPermission('delete_photo'))
const canEditCollectionCover = computed(
  () => !isSystemWorkspace.value && hasPermission('set_cover_photo'),
)
const canShare = computed(() => {
  return isSavedWorkspace.value && !isSystemWorkspace.value
})

const canBookmark = computed(() => {
  return (
    isSavedWorkspace.value &&
    !isSystemWorkspace.value &&
    workspaceStore.isPublic &&
    !isWorkspaceMember.value
  )
})
const bookmarkButtonTitle = computed(() =>
  workspaceStore.isBookmarked ? 'Remove collection bookmark' : 'Bookmark collection',
)
const isPinnedWorkspace = computed(
  () =>
    route.name === 'pinned' || (workspaceStore.isSystem && workspaceStore.systemKey === 'pinned'),
)
const activeDesktopSurface = computed<DesktopSurface>(() => {
  if (route.name === 'library' || currentView.value === 'all-activities') return 'library'
  if (isPinnedWorkspace.value) return 'pinned'
  return 'collections'
})
const isPinnedWorkspaceContentReady = computed(
  () =>
    !isPinnedWorkspace.value ||
    (workspaceStore._isLoading !== true &&
      workspaceStore.isSystem === true &&
      workspaceStore.systemKey === 'pinned' &&
      !!workspaceStore.clientId),
)
const isPinnedWorkspaceDisplayReady = isPinnedWorkspaceContentReady

const closeShareModal = () => {
  isShareModalOpen.value = false
  uiStore.setDialogOpen(false)
}

const openShareModal = () => {
  isShareModalOpen.value = true
}

const refreshCatalogAfterBookmarkChange = async () => {
  const catalog = await apiService.getWorkspacesForSearch()
  searchWorkspaceStore.setWorkspaceCatalog(catalog)
  searchWorkspaceStore.pruneUnavailableSelectedWorkspaces()
}

const handleToggleBookmark = async () => {
  if (!workspaceStore.clientId || isBookmarkBusy.value) return
  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return
  }

  isBookmarkBusy.value = true
  const nextState = !workspaceStore.isBookmarked
  try {
    if (nextState) {
      await apiService.bookmarkWorkspace(workspaceStore.clientId)
    } else {
      await apiService.unbookmarkWorkspace(workspaceStore.clientId)
    }
    workspaceStore.setBookmarkState(nextState)
    searchWorkspaceStore.updateWorkspaceBookmark(workspaceStore.clientId, nextState)
    await refreshCatalogAfterBookmarkChange()
  } catch (error) {
    showError(
      `${nextState ? 'Failed to bookmark collection' : 'Failed to remove bookmark'}: ${
        error instanceof Error ? error.message : String(error)
      }`,
    )
  } finally {
    isBookmarkBusy.value = false
  }
}

const handleOpenWorkspaceShareModal = () => {
  openShareModal()
}

const ensureCollectionBookmarkedForOffline = async (): Promise<boolean> => {
  if (!workspaceStore.clientId) return false
  if (workspaceStore.isBookmarked) return true

  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return false
  }

  try {
    await apiService.bookmarkWorkspace(workspaceStore.clientId)
    workspaceStore.setBookmarkState(true)
    searchWorkspaceStore.updateWorkspaceBookmark(workspaceStore.clientId, true)
    await refreshCatalogAfterBookmarkChange()
    return true
  } catch (error) {
    showError(
      `Failed to bookmark collection: ${error instanceof Error ? error.message : String(error)}`,
    )
    return false
  }
}

const handleDetailWorkspaceOffline = async () => {
  if (!workspaceStore.clientId) return
  if (workspaceStore.isPublic && !isWorkspaceMember.value) {
    const bookmarked = await ensureCollectionBookmarkedForOffline()
    if (!bookmarked) return
  }
  emit('make-offline', workspaceStore.clientId)
}

watch(
  () => uiStore.pendingWorkspaceShareOpen,
  (pending) => {
    if (!pending) return
    uiStore.consumeWorkspaceShareOpen()
    openShareModal()
  },
  { immediate: true },
)

const handleWorkspaceSelect = async (workspaceId: string, workspaceName: string) => {
  if (!(await requestRouteModeLeave())) return

  selectedWorkspaceName.value = workspaceName

  if (workspaceId === ALL_ACTIVITIES_WORKSPACE_ID) {
    searchWorkspaceStore.hideAllWorkspacesTemporary()
    currentView.value = 'all-activities'
    void router.push({ name: 'library' })
    return
  }

  // Hide search collections when entering a specific collection (not My Activities).
  // If coming from the Library view, collections are already temporarily hidden — skip saving
  // the current state to avoid corrupting the saved visibility with all-false.
  if (currentView.value === 'all-activities') {
    searchWorkspaceStore.hideAllWorkspacesTemporary()
  } else {
    searchWorkspaceStore.hideAllWorkspacesForNavigation()
  }
  uiStore.setWorkspacesOverview(false)

  currentView.value = 'workspace-detail'

  // Load and select the workspace
  uiStore.beginMapNavigation('collection-route')
  emit('select-workspace', workspaceId)
}

const enterPinnedPanel = () => {
  searchWorkspaceStore.hideAllWorkspacesTemporary()
  uiStore.setWorkspacesOverview(false)
  currentView.value = 'workspace-detail'
  selectedWorkspaceName.value = 'Pinned'
}

const handleDesktopSurfaceChange = async (value: string | number) => {
  const target = String(value) as DesktopSurface
  if (target === activeDesktopSurface.value) return
  if (!(await requestRouteModeLeave())) return

  descriptionExpanded.value = false
  closeCoverMenu()

  if (
    activeDesktopSurface.value === 'collections' &&
    currentView.value === 'workspace-detail' &&
    !isPinnedWorkspace.value
  ) {
    rememberLastCollectionContext(routeWorkspaceId.value ?? workspaceStore.clientId)
  }

  if (target === 'pinned') {
    searchWorkspaceStore.hideAllWorkspacesTemporary()
    uiStore.setWorkspacesOverview(false)
    uiStore.beginMapNavigation('map')
    currentView.value = 'workspace-detail'
    selectedWorkspaceName.value = 'Pinned'
    void router.push({ name: 'pinned' })
    return
  }

  if (target === 'library') {
    searchWorkspaceStore.hideAllWorkspacesTemporary()
    uiStore.setWorkspacesOverview(false)
    currentView.value = 'all-activities'
    void router.push({ name: 'library' })
    return
  }

  // Opening the Collections surface fits the map to the target content again (overview
  // bounds or the collection's content); only Pinned above keeps the current camera.
  const lastCollectionTarget = getLastCollectionRouteTarget()
  if (lastCollectionTarget.name === 'workspaces') {
    uiStore.setWorkspacesOverview(true)
    currentView.value = 'workspaces'
    selectedWorkspaceName.value = ''
    uiStore.beginMapNavigation(null)
    uiStore.enableAutoViewportAdaptation()
  } else {
    uiStore.setWorkspacesOverview(false)
    currentView.value = 'workspace-detail'
    uiStore.beginMapNavigation('collection-route')
  }
  void router.push(lastCollectionTarget)
}

const triggerLibraryImport = () => {
  window.dispatchEvent(new CustomEvent('trigger-library-import'))
}

const handleBackToWorkspaces = async () => {
  descriptionExpanded.value = false
  // If coming back from the My Activities panel, just reset the local view — no route change needed
  if (currentView.value === 'all-activities') {
    uiStore.hideTourDetailPanel()
    searchWorkspaceStore.activateAllWorkspaces()
    currentView.value = 'workspaces'
    void router.push({ name: 'workspaces' })
    return
  }

  if (!(await requestRouteModeLeave())) return

  // If we're in collections view, reload collections instead of navigating
  if (currentView.value === 'workspaces') {
    await searchWorkspaceStore.init()
    const collections = await searchWorkspaceStore.ensureWorkspacesLoaded(true)
    searchWorkspaceStore.setAllWorkspaces(collections)
    searchWorkspaceStore.activateAllWorkspaces()
    return
  }

  // Clear the current workspace state before navigation
  workspaceStore.clearToOverviewState()

  // Set UI state to collections overview
  uiStore.setWorkspacesOverview(true)
  uiStore.beginMapNavigation('back')

  // Navigate to root URL to properly exit workspace context
  router.push({ name: 'workspaces' })
}

// ─── Cover photo pick mode ────────────────────────────────────
const isPickCoverMode = ref(false)

const handleChangeCoverPhoto = () => {
  isPickCoverMode.value = true
  nextTick(() => {
    requestSectionScroll('photos')
  })
}

const handleCancelPickCover = () => {
  isPickCoverMode.value = false
}

const handlePickCoverPhoto = async (photoId: string) => {
  if (!(await requirePermission('set_cover_photo'))) return
  try {
    await workspaceStore.setCoverPhoto(photoId)
  } finally {
    isPickCoverMode.value = false
  }
}

const handleRemoveCoverPhoto = async () => {
  if (!(await requirePermission('set_cover_photo'))) return
  try {
    await workspaceStore.setCoverPhoto(null)
  } finally {
    isPickCoverMode.value = false
  }
}
// ─────────────────────────────────────────────────────────────

const triggerGpxUpload = () => {
  emit('trigger-gpx-upload')
}

const triggerPhotoUpload = () => {
  emit('trigger-photo-upload')
}

const openWorkspaceGpxDownloadModal = () => {
  if (isWorkspaceGpxDownloading.value) return
  isWorkspaceGpxDownloadModalOpen.value = true
}

const closeWorkspaceGpxDownloadModal = () => {
  isWorkspaceGpxDownloadModalOpen.value = false
}

const handleWorkspaceGpxDownload = async (options: { includeRoutePoints: boolean }) => {
  if (!workspaceStore.clientId) return

  isWorkspaceGpxDownloading.value = true
  try {
    const [blob] = await Promise.all([
      apiService.downloadWorkspaceGpx(workspaceStore.clientId, {
        includeRoutePoints: options.includeRoutePoints,
      }),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ])

    const downloadName = workspaceStore.name.trim() || 'collection'
    await downloadFile(blob, `${downloadName}.gpx`, { mimeType: 'application/gpx+xml' })
    closeWorkspaceGpxDownloadModal()
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download collection GPX', { error })
    showError(`Failed to download: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    isWorkspaceGpxDownloading.value = false
  }
}

const handleViewPhoto = (photoId: string) => {
  emit('view-photo', photoId)
}

const handlePhotoZoom = (photoId: string) => {
  emit('zoom-photo', photoId)
}

const handleOpenPOIDetail = (poiId: string) => {
  emit('open-poi-detail', poiId)
}

const handleZoomPOI = (poiId: string) => {
  emit('zoom-poi', poiId)
}

onUnmounted(() => {
  if (loadingTextTimer) {
    clearTimeout(loadingTextTimer)
    loadingTextTimer = null
  }
  if (loadingIconTimer) {
    clearTimeout(loadingIconTimer)
    loadingIconTimer = null
  }
  scrollContainer.value?.removeEventListener('scroll', updateBottomSectionHints)
})

// Deep link focus handler: scroll photos to tour start without triggering map zoom
const handleFocusTourEvent = (evt: Event) => {
  const detail = (evt as CustomEvent).detail as { tourId?: string }
  const tourId = detail?.tourId
  if (!tourId) return
  if (currentView.value !== 'workspace-detail' || !hasPhotos.value) return

  const start = getTourStartTime(tourId)
  let targetPhotoId: string | null = null
  if (start) targetPhotoId = findNearestPhotoIdByTime(start)
  if (!targetPhotoId) {
    const loc = getTourStartLocation(tourId)
    if (loc) targetPhotoId = findNearestPhotoIdByLocation(loc.lat, loc.lon)
  }
  if (!targetPhotoId) return
  nextTick(() => requestAnimationFrame(() => scrollPhotosToPhoto(targetPhotoId!)))
}

useEventListener(window, 'focus-tour', handleFocusTourEvent)
useEventListener(window, 'open-workspace-share-modal', handleOpenWorkspaceShareModal)
useEventListener(window, 'open-pinned-surface', enterPinnedPanel)
useEventListener(window, 'open-all-activities', () => {
  if (currentView.value !== 'all-activities') {
    searchWorkspaceStore.hideAllWorkspacesTemporary()
    currentView.value = 'all-activities'
  }
})

useEventListener(document, 'keydown', (e: KeyboardEvent) => {
  if (currentView.value !== 'workspace-detail') return
  const key = typeof e.key === 'string' ? e.key.toLowerCase() : ''
  const target = e.target instanceof HTMLElement ? e.target : null
  const isTyping =
    target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable
  if (key === 's' && !isTyping && !e.repeat && !e.ctrlKey && !e.metaKey) {
    e.preventDefault()
    tourLockListToMap.value = !tourLockListToMap.value
  }
})

watch(
  () => currentView.value,
  () =>
    nextTick(() => {
      // Reset scroll position to top when switching views
      const container = scrollContainer.value
      if (container) {
        container.scrollTop = 0
      }
    }),
)

// Drive isAllActivitiesView from navigation state, not component lifecycle,
// so tracks only appear when the user is actually in the all-activities view.
watch(
  () => currentView.value,
  (val) => {
    uiStore.isAllActivitiesView = val === 'all-activities'
  },
  { immediate: true },
)

// Compute earliest start time for a tour
const getTourStartTime = (tourId: string): number | null => {
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) return null
  const times = tour.tracks
    .map((t) => t.startTime)
    .filter((v): v is number => typeof v === 'number' && !isNaN(v))
  if (times.length === 0) return null
  return Math.min(...times)
}

// Find photo id with takenAt nearest to timestamp
const findNearestPhotoIdByTime = (timestamp: number): string | null => {
  const list = photosSortedByDate?.value ?? []
  const timed = list.filter((p) => typeof p.takenAt === 'number')
  if (timed.length === 0) return null
  let best: { id: string; diff: number } | null = null
  for (const p of timed) {
    const diff = Math.abs((p.takenAt as number) - timestamp)
    if (!best || diff < best.diff) best = { id: p.clientId, diff }
  }
  return best ? best.id : null
}

// Get the tour start location (lat/lon) by using the earliest-starting track if available,
// or falling back to the first available point
const getTourStartLocation = (tourId: string): { lat: number; lon: number } | null => {
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) return null
  if (!tour.tracks || tour.tracks.length === 0) return null

  // Prefer the track with the smallest startTime
  const tracks = tour.tracks.slice()
  tracks.sort((a, b) => {
    const aTime = typeof a.startTime === 'number' ? a.startTime : Number.POSITIVE_INFINITY
    const bTime = typeof b.startTime === 'number' ? b.startTime : Number.POSITIVE_INFINITY
    return aTime - bTime
  })

  for (const track of tracks) {
    for (const segment of track.segments ?? []) {
      const first = segment.points?.[0]
      if (first && typeof first.lat === 'number' && typeof first.lon === 'number') {
        return { lat: first.lat, lon: first.lon }
      }
    }
    // Fallback: parse reduced LINESTRING coordinates from API and take first point
    const lineString = getBestTrackLineString(track)
    if (lineString) {
      const coords = parseLineString(lineString)
      if (coords.length > 0) {
        const [lng, lat] = coords[0]
        if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
          return { lat, lon: lng }
        }
      }
    }
  }
  return null
}

// Find nearest photo by geographic distance to a given location
const findNearestPhotoIdByLocation = (lat: number, lon: number): string | null => {
  const list = photosSortedByDate?.value ?? []
  const withCoords = list.filter(
    (p) => p.hasCoordinates && typeof p.lat === 'number' && typeof p.lon === 'number',
  )
  if (withCoords.length === 0) {
    return null
  }
  let best: { id: string; dist: number } | null = null
  for (const p of withCoords) {
    const d = calculateHaversineDistance(lat, lon, p.lat as number, p.lon as number)
    if (!best || d < best.dist) best = { id: p.clientId, dist: d }
  }
  return best ? best.id : null
}

// Scroll the photos list to ensure a given photo tile is visible below sticky header
const scrollPhotosToPhoto = (photoId: string) => {
  const container = scrollContainer.value
  if (!container) {
    return
  }
  const el = container.querySelector(`[data-photo-id="${photoId}"]`) as HTMLElement | null
  if (!el) {
    return
  }
  const header = photosHeaderRef.value
  const headerHeight = header ? header.offsetHeight : 0
  const cRect = container.getBoundingClientRect()
  const eRect = el.getBoundingClientRect()
  const targetTop = container.scrollTop + (eRect.top - cRect.top) - headerHeight - 8
  container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
}

const handleZoomTour = (tourId: string) => {
  emit('zoom-tour', tourId)
}

const handleSelectTour = (tourId: string) => {
  emit('select-tour', tourId)
}

const handleMakeOffline = (workspaceId: string) => {
  emit('make-offline', workspaceId)
}

const handleEnterTrackEdit = (tourId: string, trackClientId: string) => {
  emit('enter-track-edit', tourId, trackClientId)
}

const handleToggleAllToursVisibility = async () => {
  if (!hasTours.value) return
  await workspaceStore.setAllToursVisibility(!isAnyTourVisible.value)
}

const handleCreateRoute = async () => {
  if (!(await requirePermission('edit_track'))) return
  isCreateTourModalOpen.value = true
}

const handleCreateRouteFromModal = async ({ type }: { type?: string }) => {
  isCreateTourModalOpen.value = false
  const tourId = await libraryStore.createEmptyTour({ type })
  if (!tourId) return
  await nextTick()
  window.dispatchEvent(
    new CustomEvent('start-track-drawing', {
      detail: { tourId, scope: 'library', discardTourOnCancel: true },
    }),
  )
}

const handleDrawRouteIntoCollection = async () => {
  if (!(await requirePermission('edit_track'))) return
  const tourId = workspaceStore.addLocalDraftTour()
  await nextTick()
  window.dispatchEvent(
    new CustomEvent('start-track-drawing', {
      detail: { tourId, scope: 'workspace', discardTourOnCancel: true },
    }),
  )
}

const handleTogglePhotosVisibility = () => {
  uiStore.setPhotosVisible(!arePhotosVisible.value)
}

const collapseSidebar = () => {
  uiStore.hideSidebar()
}

// ─────────────────────────────────────────────────────────────
// Collection Detail — search / filter / sort
// ─────────────────────────────────────────────────────────────

const { lockListToMap: tourLockListToMap, currentMapBounds: tourMapBounds } = useSyncToMap(
  UI_SETTING_KEYS.workspaceSearchLockListToMap,
)

type TourSortBy = 'position' | 'name' | 'distance' | 'date'
type SortDir = 'asc' | 'desc'

const tourSearchQuery = ref('')
const tourSortBy = ref<TourSortBy>('date')
const tourSortDir = ref<SortDir>('asc')

const loadTourSortPreference = () => {
  const saved = settingsService.getSetting<{ sortBy?: TourSortBy; sortDir?: SortDir }>(
    UI_SETTING_KEYS.mapTourSort,
  )
  if (saved?.sortBy && saved.sortDir) {
    tourSortBy.value = saved.sortBy
    tourSortDir.value = saved.sortDir
  }
}

const saveTourSortPreference = () => {
  settingsService.saveSetting(UI_SETTING_KEYS.mapTourSort, {
    sortBy: tourSortBy.value,
    sortDir: tourSortDir.value,
  })
}

loadTourSortPreference()

// Sort menu
const tourSortMenuOpen = ref(false)
const tourSortMenuStyle = ref<{ top?: string; bottom?: string; right: string }>({
  top: '0px',
  right: '0px',
})

const toggleTourSortMenu = (event: MouseEvent) => {
  if (tourSortMenuOpen.value) {
    tourSortMenuOpen.value = false
    return
  }
  closeCoverMenu()
  const button = event.currentTarget as HTMLElement
  const rect = button.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const right = window.innerWidth - rect.right
  if (spaceBelow >= 200) {
    tourSortMenuStyle.value = { top: `${rect.bottom + 4}px`, right: `${right}px` }
  } else {
    tourSortMenuStyle.value = {
      bottom: `${window.innerHeight - rect.top + 4}px`,
      right: `${right}px`,
    }
  }
  tourSortMenuOpen.value = true
}

const closeTourSortMenu = () => {
  tourSortMenuOpen.value = false
}

const tourSortOptions: Array<{ label: string; sortBy: TourSortBy; sortDir: SortDir }> = [
  { label: 'Manual order', sortBy: 'position', sortDir: 'asc' },
  { label: 'Name (A to Z)', sortBy: 'name', sortDir: 'asc' },
  { label: 'Name (Z to A)', sortBy: 'name', sortDir: 'desc' },
  { label: 'Distance (short to long)', sortBy: 'distance', sortDir: 'asc' },
  { label: 'Distance (long to short)', sortBy: 'distance', sortDir: 'desc' },
  { label: 'Date (new to old)', sortBy: 'date', sortDir: 'desc' },
  { label: 'Date (old to new)', sortBy: 'date', sortDir: 'asc' },
]

const isActiveTourSort = (opt: { sortBy: TourSortBy; sortDir: SortDir }) =>
  tourSortBy.value === opt.sortBy && tourSortDir.value === opt.sortDir

const setTourSort = (opt: { sortBy: TourSortBy; sortDir: SortDir }) => {
  tourSortBy.value = opt.sortBy
  tourSortDir.value = opt.sortDir
  closeTourSortMenu()
  saveTourSortPreference()
}

const statsExpanded = ref(
  settingsService.getSetting<boolean>(UI_SETTING_KEYS.mapStatsExpanded) === true,
)
const toggleStatsExpanded = () => {
  statsExpanded.value = !statsExpanded.value
  settingsService.saveSetting(UI_SETTING_KEYS.mapStatsExpanded, statsExpanded.value)
}

settingsService.subscribe(({ key, value, source }) => {
  if (source !== 'remote') return

  if (key === UI_SETTING_KEYS.mapStatsExpanded) {
    statsExpanded.value = value === true
  } else if (key === UI_SETTING_KEYS.mapTourSort && value && typeof value === 'object') {
    const saved = value as { sortBy?: TourSortBy; sortDir?: SortDir }
    if (saved.sortBy && saved.sortDir) {
      tourSortBy.value = saved.sortBy
      tourSortDir.value = saved.sortDir
    }
  }
})

const collectionDateRange = computed(() => {
  const times = workspaceStore.allTours
    .flatMap((t) => t.tracks.map((tr) => tr.startTime))
    .filter((s): s is number => s != null && isFinite(s))
  if (times.length === 0) return null
  const minTs = Math.min(...times)
  const maxTs = Math.max(...times)
  const minD = new Date(minTs)
  const maxD = new Date(maxTs)
  const fmtFull = (d: Date) =>
    d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  const fmtNoYear = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  if (minD.toDateString() === maxD.toDateString()) return fmtFull(minD)
  if (minD.getFullYear() === maxD.getFullYear()) return `${fmtNoYear(minD)} – ${fmtFull(maxD)}`
  return `${fmtFull(minD)} – ${fmtFull(maxD)}`
})

const daysUntilDeletion = computed(() => getDaysUntilDeletion(workspaceStore.deletionScheduledAt))
const canDeleteWorkspace = computed(
  () =>
    isSavedWorkspace.value &&
    !isSystemWorkspace.value &&
    hasPermission('delete_workspace') &&
    !workspaceStore.markedForDeletion,
)
const canRestoreWorkspace = computed(
  () =>
    isSavedWorkspace.value &&
    !isSystemWorkspace.value &&
    hasPermission('delete_workspace') &&
    workspaceStore.markedForDeletion,
)

// An invited member (Collaborator/Viewer) can leave; owners delete instead. Mutually exclusive
// with canDeleteWorkspace, so Leave and Delete never appear together.
const canLeaveWorkspace = computed(
  () => isSavedWorkspace.value && !workspaceStore.isOwner && !workspaceStore.markedForDeletion,
)

const handleRestoreCollection = async () => {
  try {
    await workspaceStore.restoreWorkspace()
  } catch (error) {
    logService.warn(LOG_CATEGORIES.GENERAL, 'Collection restore request failed', {
      workspaceId: workspaceStore.clientId,
      error,
    })
  }
}

const handleDeleteCollection = async () => {
  closeCoverMenu()
  if (!(await requirePermission('delete_workspace'))) return
  const confirmed = await showConfirm({
    title: 'Delete Collection?',
    message: `Are you sure you want to delete "${workspaceStore.name}"?\n\nThe collection will be marked for deletion and permanently removed after 7 days.\n\nOnly owners can access the workspace during this period. You won't find it in search, but it can be restored if you know the link.`,
    confirmText: 'Delete Collection',
    cancelText: 'Cancel',
    variant: 'danger',
  })
  if (confirmed) {
    try {
      await workspaceStore.markWorkspaceForDeletion()
      await handleBackToWorkspaces()
    } catch (error) {
      logService.warn(LOG_CATEGORIES.GENERAL, 'Collection deletion request failed', {
        workspaceId: workspaceStore.clientId,
        error,
      })
    }
  }
}

const handleLeaveCollection = async () => {
  closeCoverMenu()
  const confirmed = await showConfirm({
    title: 'Leave collection?',
    message: `Are you sure you want to leave "${workspaceStore.name}"?\n\nYou will lose access to this collection. You can only rejoin if you're invited again.`,
    confirmText: 'Leave Collection',
    cancelText: 'Cancel',
    variant: 'danger',
  })
  if (confirmed) {
    try {
      await workspaceStore.leaveWorkspace()
      await handleBackToWorkspaces()
    } catch (error) {
      logService.warn(LOG_CATEGORIES.GENERAL, 'Collection leave request failed', {
        workspaceId: workspaceStore.clientId,
        error,
      })
    }
  }
}

const coverMenuOpen = ref(false)
const coverMenuStyle = ref<{ top?: string; bottom?: string; left?: string; right?: string }>({
  top: '0px',
  left: '0px',
})

const toggleCoverMenu = (event: MouseEvent) => {
  if (coverMenuOpen.value) {
    coverMenuOpen.value = false
    return
  }
  closeTourSortMenu()
  const button = event.currentTarget as HTMLElement
  const rect = button.getBoundingClientRect()
  coverMenuStyle.value = {
    top: `${rect.bottom + 4}px`,
    right: `${window.innerWidth - rect.right}px`,
  }
  coverMenuOpen.value = true
}

const closeCoverMenu = () => {
  coverMenuOpen.value = false
}

const handleUnpinAll = async () => {
  closeCoverMenu()
  if (!hasItemsToUnpin.value) return
  if (!(await requireOnlineModificationAccess('unpin all items'))) return

  const confirmed = await showConfirm({
    title: 'Unpin everything?',
    message:
      'This removes every activity and route from Pinned, plus POIs pinned from elsewhere. POIs and photos created directly in Pinned stay here. No data will be deleted.',
    confirmText: 'Unpin all',
    cancelText: 'Cancel',
    variant: 'warning',
  })
  if (!confirmed) return

  try {
    const result = await unpinAllPinnedItems({
      tours: workspaceStore.tours,
      pois: workspaceStore.allWorkspacePOIs,
    })
    if (result.failedCount > 0) {
      showError(
        `Unpinned ${result.unpinnedCount} item${result.unpinnedCount === 1 ? '' : 's'}. ${result.failedCount} item${result.failedCount === 1 ? '' : 's'} could not be unpinned and remain in Pinned.`,
      )
    } else {
      showSuccess('Unpinned all linked items')
    }
    collapseSidebar()
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to clear Pinned', { error })
    showError(`Failed to clear Pinned: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const handleCurrentWorkspacePinToggle = async () => {
  closeCoverMenu()
  if (!hasCurrentWorkspacePinnableItems.value) return

  const shouldPin = !areAllCurrentWorkspaceItemsPinned.value
  if (!(await requireOnlineModificationAccess(shouldPin ? 'pin all items' : 'unpin all items'))) {
    return
  }

  try {
    await setPinnedItemsBatch(
      [{ tours: workspaceStore.tours, pois: workspaceStore.allWorkspacePOIs }],
      shouldPin,
    )
    showSuccess(shouldPin ? 'Pinned all items' : 'Unpinned all items')
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update collection Pinned items', {
      error,
      workspaceId: workspaceStore.clientId,
    })
    showError(`Failed to update Pinned: ${error instanceof Error ? error.message : String(error)}`)
  }
}

// Rename workspace inline
const isEditingWorkspaceName = ref(false)
const editedWorkspaceName = ref('')
const workspaceNameError = ref('')
const workspaceNameInputRef = ref<HTMLInputElement | null>(null)

const startRenamingWorkspace = async () => {
  closeCoverMenu()
  if (!(await requirePermission('rename_workspace'))) return
  editedWorkspaceName.value = workspaceStore.name || ''
  workspaceNameError.value = ''
  isEditingWorkspaceName.value = true
  nextTick(() => workspaceNameInputRef.value?.focus())
}

const saveWorkspaceName = async () => {
  if (!isEditingWorkspaceName.value) return
  const trimmed = editedWorkspaceName.value.trim()
  if (!trimmed) {
    workspaceNameError.value = 'Name cannot be empty'
    return
  }
  if (trimmed.length > 255) {
    workspaceNameError.value = 'Name must be 255 characters or fewer'
    return
  }
  if (!(await requirePermission('rename_workspace'))) return
  workspaceStore.updateWorkspace({ name: trimmed })
  isEditingWorkspaceName.value = false
  workspaceNameError.value = ''
}

const cancelRenamingWorkspace = () => {
  isEditingWorkspaceName.value = false
  workspaceNameError.value = ''
}

const handleOpenAddPOIModal = () => {
  window.dispatchEvent(new CustomEvent('open-add-poi-modal'))
}

const requestCollectionDrawRoute = async () => {
  if (currentView.value !== 'workspace-detail') return
  await handleDrawRouteIntoCollection()
}

const requestCollectionImportFiles = () => {
  if (currentView.value !== 'workspace-detail') return
  triggerGpxUpload()
}

const requestCollectionPOI = () => {
  if (currentView.value !== 'workspace-detail') return
  window.dispatchEvent(new CustomEvent('open-add-poi-modal'))
}

const navigatePinnedEmptyStateToLibrary = () => {
  void router.push({ name: 'library' })
}

const navigatePinnedEmptyStateToCollections = () => {
  void router.push({ name: 'workspaces' })
}

useEventListener(window, 'workspace-add-draw-route', () => {
  void requestCollectionDrawRoute()
})
useEventListener(window, 'workspace-add-import-files', requestCollectionImportFiles)
useEventListener(window, 'workspace-add-poi', requestCollectionPOI)

const handleTogglePOIsVisibility = () => {
  uiStore.setPOIsVisible(!uiStore.arePOIsVisible)
}

// Close menus/search on outside click
useEventListener(document, 'click', (event: Event) => {
  const target = event.target as HTMLElement | null
  if (!target?.closest('[data-tour-sort-menu]')) closeTourSortMenu()
  if (!target?.closest('[data-cover-menu]')) closeCoverMenu()
})

// Helper: check if any track point of a tour is within the current map viewport
const isTourInViewport = (tour: import('@/types/tour').Tour): boolean => {
  const vp = tourMapBounds.value
  if (!vp) return true
  for (const track of tour.tracks) {
    // Prefer reduced line if available
    if (track.pointsLineReduced500m) {
      const coords = parseLineString(track.pointsLineReduced500m)
      for (const [lng, lat] of coords) {
        if (lng >= vp.west && lng <= vp.east && lat >= vp.south && lat <= vp.north) return true
      }
    } else {
      for (const seg of track.segments) {
        for (const pt of seg.points) {
          if (pt.lon >= vp.west && pt.lon <= vp.east && pt.lat >= vp.south && pt.lat <= vp.north)
            return true
        }
      }
    }
  }
  return false
}

// Filtered + sorted tour list; null means "no override" (show all in store order with drag)
const detailDisplayTours = computed(() => {
  const allTours = workspaceStore.allTours
  const query = tourSearchQuery.value.toLowerCase().trim()
  const isFiltering = query !== '' || tourLockListToMap.value || tourSortBy.value !== 'position'

  if (!isFiltering) return undefined // let TourList use store directly with drag enabled

  let result = [...allTours]

  // 1. Search filter
  if (query) {
    result = result.filter((t) => t.name.toLowerCase().includes(query))
  }

  // 2. Sync-to-map filter
  if (tourLockListToMap.value && tourMapBounds.value) {
    result = result.filter(isTourInViewport)
  }

  // 3. Sort
  result.sort((a, b) => {
    let cmp = 0
    switch (tourSortBy.value) {
      case 'name':
        cmp = a.name.localeCompare(b.name)
        break
      case 'distance': {
        const calcDist = (t: import('@/types/tour').Tour) =>
          t.tracks.reduce((sum, tr) => sum + calculateTrackDistance(tr), 0)
        cmp = calcDist(a) - calcDist(b)
        break
      }
      case 'date': {
        const getTime = (t: import('@/types/tour').Tour) => {
          const minStart = Math.min(
            ...t.tracks.map((tr) => tr.startTime ?? Infinity).filter(isFinite),
          )
          if (isFinite(minStart)) return minStart
          return t.createdAt ? new Date(t.createdAt).getTime() : Infinity
        }
        const ta = getTime(a)
        const tb = getTime(b)
        cmp = isFinite(ta) && isFinite(tb) ? ta - tb : isFinite(ta) ? -1 : isFinite(tb) ? 1 : 0
        break
      }
      default:
        cmp = 0
    }
    return tourSortDir.value === 'asc' ? cmp : -cmp
  })

  return result
})

const totalTourCount = computed(() => workspaceStore.allTours.length)
const filteredTourCount = computed(() => detailDisplayTours.value?.length ?? totalTourCount.value)

const findNearestScrollableAncestor = (element: HTMLElement): HTMLElement | null => {
  let current = element.parentElement

  while (current) {
    const style = window.getComputedStyle(current)
    const overflowY = style.overflowY
    const isScrollable =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight
    if (isScrollable) {
      return current
    }
    current = current.parentElement
  }

  return null
}

const requestSectionScroll = (section: SectionScrollTarget) => {
  pendingSectionScroll.value = section
}

const getSectionAnchor = (section: SectionScrollTarget): HTMLElement | null => {
  switch (section) {
    case 'tours':
      return toursSectionAnchorRef.value
    case 'poi':
      return poiSectionAnchorRef.value
    case 'photos':
      return photosSectionAnchorRef.value
  }
}

const DETAIL_SECTION_HEADER_HEIGHT = 38
const detailSectionOrder: SectionScrollTarget[] = ['tours', 'poi', 'photos']

const getSectionHeader = (section: SectionScrollTarget): HTMLElement | null => {
  switch (section) {
    case 'tours':
      return toursHeaderInstance.value?.rootEl ?? null
    case 'poi':
      return poiHeaderInstance.value?.rootEl ?? null
    case 'photos':
      return photosHeaderRef.value
  }
}

const getSectionScrollTopOffset = (section: SectionScrollTarget) => {
  const sectionIndex = detailSectionOrder.indexOf(section)
  if (sectionIndex <= 0) {
    return 0
  }

  return sectionIndex * DETAIL_SECTION_HEADER_HEIGHT
}

const setTopSectionHintVisibility = (section: SectionScrollTarget, visible: boolean) => {
  switch (section) {
    case 'tours':
      showToursTopHint.value = visible
      return
    case 'poi':
      showPOITopHint.value = visible
      return
    case 'photos':
      showPhotosTopHint.value = visible
      return
  }
}

const setBottomSectionHintVisibility = (section: SectionScrollTarget, visible: boolean) => {
  switch (section) {
    case 'tours':
      showToursBottomHint.value = visible
      return
    case 'poi':
      showPOIBottomHint.value = visible
      return
    case 'photos':
      showPhotosBottomHint.value = visible
      return
  }
}

const resetSectionHints = () => {
  showToursTopHint.value = false
  showPOITopHint.value = false
  showPhotosTopHint.value = false
  showToursBottomHint.value = false
  showPOIBottomHint.value = false
  showPhotosBottomHint.value = false
}

const updateSectionHints = () => {
  const container = scrollContainer.value
  if (!container || currentView.value !== 'workspace-detail' || isDetailLoading.value) {
    resetSectionHints()
    return
  }

  const containerRect = container.getBoundingClientRect()
  const headerRects = new Map<SectionScrollTarget, DOMRect>()
  const isAtTopOfContainer = container.scrollTop <= 1

  for (const section of detailSectionOrder) {
    const header = getSectionHeader(section)
    const headerRect = header?.getBoundingClientRect() ?? null
    if (!headerRect) {
      resetSectionHints()
      return
    }
    headerRects.set(section, headerRect)
  }

  let visibleTopHints = 0
  for (const section of detailSectionOrder) {
    const headerRect = headerRects.get(section)
    if (!headerRect) continue

    const topHintBoundary = containerRect.top + DETAIL_SECTION_HEADER_HEIGHT * visibleTopHints
    const shouldShowHint =
      !isAtTopOfContainer &&
      (visibleTopHints === 0
        ? headerRect.top < containerRect.top
        : headerRect.top <= topHintBoundary)
    setTopSectionHintVisibility(section, shouldShowHint)
    if (shouldShowHint) {
      visibleTopHints += 1
    }
  }

  let visibleBottomHints = 0
  for (const section of [...detailSectionOrder].reverse()) {
    const headerRect = headerRects.get(section)
    if (!headerRect) continue

    const hintTop = containerRect.bottom - DETAIL_SECTION_HEADER_HEIGHT * (visibleBottomHints + 1)
    const shouldShowHint = headerRect.top >= hintTop
    setBottomSectionHintVisibility(section, shouldShowHint)
    if (shouldShowHint) {
      visibleBottomHints += 1
    }
  }
}

const scheduleSectionHintUpdate = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      updateSectionHints()
    })
  })
}

const handleWorkspaceDetailAfterEnter = () => {
  scheduleSectionHintUpdate()
}

const scheduleBottomSectionHintUpdate = () => {
  scheduleSectionHintUpdate()
}

const updateBottomSectionHints = () => {
  updateSectionHints()
}

// Info popover state (shared for tour and photo headers)
const infoPopoverOpen = ref(false)
const infoPopoverStyle = ref<Record<string, string>>({})

const closeInfoPopover = () => {
  infoPopoverOpen.value = false
}

useEventListener(document, 'click', (event: Event) => {
  const target = event.target as HTMLElement | null
  if (
    !target?.closest('[data-detail-info-popover]') &&
    !target?.closest('[data-info-popover-btn]')
  ) {
    closeInfoPopover()
  }
})

const countInfoPopoverOpen = ref(false)
const countInfoPopoverStyle = ref<Record<string, string>>({})

const openCountInfoPopover = (event: MouseEvent) => {
  const btn = event.currentTarget as HTMLElement
  const rect = btn.getBoundingClientRect()
  const popoverWidth = 240
  const left = Math.min(rect.left, window.innerWidth - popoverWidth - 8)
  countInfoPopoverStyle.value = {
    top: `${rect.bottom + 4}px`,
    left: `${Math.max(8, left)}px`,
  }
  countInfoPopoverOpen.value = true
}

const closeCountInfoPopover = () => {
  countInfoPopoverOpen.value = false
}

useEventListener(document, 'click', (event: Event) => {
  const target = event.target as HTMLElement | null
  if (!target?.closest('[data-count-info-popover]') && !target?.closest('[data-count-info-btn]')) {
    closeCountInfoPopover()
  }
})

// Photo map filtering — photos are not synced to map
const filteredPhotoList = computed(() => {
  return photosSortedByDate.value ?? []
})

const totalPhotoCount = computed(() => photosSortedByDate.value?.length ?? 0)
const filteredPhotoCount = computed(() => filteredPhotoList.value.length)
const isPhotoFiltering = computed(() => filteredPhotoCount.value !== totalPhotoCount.value)

// Initialize search collections when component mounts in collections view
onMounted(() => {
  // Use route params as the source of truth — same reason as hasActiveWorkspace above:
  // workspaceStore.clientId is always truthy on init and can't distinguish "no workspace".
  const routeWorkspaceIdOnMount =
    typeof route.params.workspaceId === 'string' && route.params.workspaceId
      ? route.params.workspaceId
      : null

  if (routeWorkspaceIdOnMount || isPinnedRoute()) {
    currentView.value = 'workspace-detail'
    selectedWorkspaceName.value = isPinnedRoute()
      ? 'Pinned'
      : workspaceStore.name || 'Untitled Collection'
  } else {
    currentView.value = 'workspaces'
  }
})

// Watch for workspace changes to update the view
watch(
  () => workspaceStore.clientId,
  (newClientId) => {
    isPickCoverMode.value = false
    if (newClientId) {
      // A workspace was loaded, switch to collection detail view
      currentView.value = 'workspace-detail'
      selectedWorkspaceName.value = workspaceStore.name || 'Untitled Collection'
    } else {
      // No workspace loaded, switch to collections overview
      currentView.value = 'workspaces'
      selectedWorkspaceName.value = ''
    }
  },
)

// Watch for workspace name changes to update the displayed name
watch(
  () => workspaceStore.name,
  (newName) => {
    if (currentView.value === 'workspace-detail') {
      selectedWorkspaceName.value = newName || 'Untitled Collection'
    }
  },
)

// ─────────────────────────────────────────────────────────────
// Sidebar width
// ─────────────────────────────────────────────────────────────

const SIDEBAR_DEFAULT_WIDTH = 480

const sidebarPanelRef = ref<HTMLElement | null>(null)
const activityRouteCollectionRef = ref<InstanceType<typeof ActivityRouteCollection> | null>(null)
const sidebarMaxHeightPx = ref<number>(window.innerHeight - 108)

const updateSidebarMaxHeight = () => {
  const panel = sidebarPanelRef.value
  if (!panel) return
  const rect = panel.getBoundingClientRect()
  const available = Math.floor(window.innerHeight - rect.top - 48)
  sidebarMaxHeightPx.value = Math.max(320, available)
}

useEventListener(window, 'resize', () => {
  updateSidebarMaxHeight()
  scheduleBottomSectionHintUpdate()
})

onMounted(() => {
  updateSidebarMaxHeight()
  scheduleBottomSectionHintUpdate()
})

watch(currentView, () => {
  resetSectionHints()
  nextTick(() => {
    updateSidebarMaxHeight()
  })
  scheduleBottomSectionHintUpdate()
})

watch(scrollContainer, (container, previousContainer) => {
  previousContainer?.removeEventListener('scroll', updateBottomSectionHints)
  container?.addEventListener('scroll', updateBottomSectionHints, { passive: true })
  scheduleBottomSectionHintUpdate()
})

watch(
  [
    currentView,
    isDetailLoading,
    hasTours,
    hasPOIs,
    hasPhotos,
    () => detailDisplayTours.value?.length ?? 0,
    () => filteredPhotoList.value.length,
    () => workspaceStore.allWorkspacePOIs.length,
  ],
  () => {
    scheduleBottomSectionHintUpdate()
  },
  { flush: 'post' },
)

watch(
  () => pendingSectionScroll.value,
  (section) => {
    if (!section) {
      return
    }

    nextTick(() => {
      requestAnimationFrame(() => {
        const target = getSectionAnchor(section)

        if (!target) {
          pendingSectionScroll.value = null
          return
        }

        const scrollParent = findNearestScrollableAncestor(target)
        if (!scrollParent) {
          target.scrollIntoView({ block: 'start', behavior: 'smooth' })
          pendingSectionScroll.value = null
          return
        }

        const parentRect = scrollParent.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        const targetTop =
          scrollParent.scrollTop +
          (targetRect.top - parentRect.top) -
          getSectionScrollTopOffset(section)
        scrollParent.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
        pendingSectionScroll.value = null
      })
    })
  },
)

const sidebarPanelStyle = computed(() => ({
  width: `${SIDEBAR_DEFAULT_WIDTH}px`,
  maxHeight: `${sidebarMaxHeightPx.value}px`,
}))

defineExpose({
  openManageCollectionsByTourId: (tourId: string, position?: { x: number; y: number }) =>
    activityRouteCollectionRef.value?.openManageCollectionsByTourId(tourId, position),
  openAddToCollectionByTourId: (tourId: string, position?: { x: number; y: number }) =>
    activityRouteCollectionRef.value?.openAddToCollectionByTourId(tourId, position),
})
</script>

<template>
  <div
    ref="sidebarPanelRef"
    data-testid="workspace-sidebar-panel"
    :class="'flex flex-col bg-stone-100 border border-black/8 rounded-xl shadow-lg m-2 overflow-hidden'"
    :style="sidebarPanelStyle"
  >
    <!-- Header Section -->
    <div class="bg-white border-gray-200 rounded-t-xl shrink-0 overflow-x-hidden">
      <div class="border-b border-gray-200 bg-white p-2">
        <BaseSegmentedControl
          :model-value="activeDesktopSurface"
          :options="desktopSurfaceOptions"
          aria-label="Desktop app surfaces"
          option-id-prefix="desktop-surface-tab"
          @update:model-value="handleDesktopSurfaceChange"
        />
      </div>
      <div v-if="currentView === 'all-activities'" class="flex items-center h-14 px-3 gap-1">
        <h1 class="truncate text-2xl font-semibold tracking-tight text-gray-900 min-w-0 flex-1">
          Library
        </h1>
        <button
          @click="handleCreateRoute"
          :disabled="isContentLoading"
          class="flex items-center justify-center w-8 h-8 text-gray-900 hover:text-gray-700 rounded transition-colors hover:cursor-pointer shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Create new route"
          aria-label="Create new route"
        >
          <PlusIcon class="w-4 h-4" />
        </button>
        <button
          @click="triggerLibraryImport"
          class="flex items-center justify-center w-8 h-8 text-gray-900 hover:text-gray-700 rounded transition-colors hover:cursor-pointer shrink-0"
          title="Import GPX or FIT files"
          aria-label="Import files"
        >
          <UploadIcon class="w-4 h-4" />
        </button>
        <button
          @click="collapseSidebar"
          data-testid="workspace-sidebar-hide"
          class="flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-800 hover:bg-black/[0.04] rounded-md transition-colors hover:cursor-pointer shrink-0"
          title="Hide sidebar"
        >
          <PanelTopCloseIcon class="h-[18px] w-[18px]" />
        </button>
      </div>
      <div v-else-if="currentView === 'workspaces'" class="grid h-14 grid-cols-3 items-center px-3">
        <!-- Col 1: title -->
        <button
          @click="handleZoomToCollections"
          class="flex items-center min-w-0 text-left hover:text-gray-600 transition-colors hover:cursor-pointer rounded"
          title="Zoom to collections"
        >
          <h1 class="truncate text-2xl font-semibold tracking-tight text-gray-900">Collections</h1>
        </button>
        <!-- Col 2: count + info (centered) -->
        <div class="flex items-center justify-center">
          <template v-if="searchWorkspaceStore.overviewCounts">
            <span class="text-xs text-gray-400">{{
              searchWorkspaceStore.overviewCounts.displayed <
              searchWorkspaceStore.overviewCounts.total
                ? `${searchWorkspaceStore.overviewCounts.displayed}/${searchWorkspaceStore.overviewCounts.total}`
                : `${searchWorkspaceStore.overviewCounts.total}`
            }}</span>
            <button
              data-count-info-btn
              @click.stop="
                countInfoPopoverOpen ? closeCountInfoPopover() : openCountInfoPopover($event)
              "
              class="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-gray-600 rounded transition-colors hover:cursor-pointer ml-0.5"
              title="Filter info"
            >
              <InfoIcon class="w-3.5 h-3.5" />
            </button>
          </template>
        </div>
        <Teleport to="body">
          <div
            v-if="countInfoPopoverOpen && searchWorkspaceStore.overviewCounts"
            :style="countInfoPopoverStyle"
            data-count-info-popover
            class="fixed z-9999 w-60 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-xs text-gray-600 leading-relaxed"
          >
            <p class="font-medium text-gray-800 mb-1">Collections count</p>
            <p>
              Showing
              <span class="font-medium">{{ searchWorkspaceStore.overviewCounts.displayed }}</span>
              of
              <span class="font-medium">{{ searchWorkspaceStore.overviewCounts.total }}</span>
              collections.
            </p>
            <p class="mt-1 text-gray-500">
              Use the Source, Sharing, and In view chips to filter the list.
            </p>
          </div>
        </Teleport>
        <!-- Col 3: actions (right-aligned) -->
        <div class="flex items-center justify-end gap-1">
          <button
            @click="emit('create-new-workspace')"
            class="flex items-center justify-center w-8 h-8 text-gray-900 hover:text-gray-700 hover:bg-black/[0.04] rounded-md transition-colors hover:cursor-pointer"
            title="Create new collection"
            aria-label="Create new collection"
          >
            <CreateCollectionIcon class="h-4 w-4" />
          </button>
          <button
            @click="collapseSidebar"
            data-testid="workspace-sidebar-hide"
            class="flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-800 hover:bg-black/[0.04] rounded-md transition-colors hover:cursor-pointer"
            title="Hide sidebar"
          >
            <PanelTopCloseIcon class="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
      <div v-else class="relative">
        <div
          v-if="isPinnedWorkspace"
          class="flex items-center gap-2 border-b border-gray-200 bg-white px-3 py-2"
        >
          <h2 class="min-w-0 flex-1 truncate text-2xl font-semibold tracking-tight text-gray-900">
            Pinned
          </h2>
          <div class="relative" data-cover-menu>
            <button
              @click.stop="toggleCoverMenu"
              class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 hover:cursor-pointer"
              :class="coverMenuOpen ? 'bg-gray-100 text-gray-800' : ''"
              title="More options"
              aria-label="More options"
            >
              <MoreHorizontalIcon class="h-5 w-5" />
            </button>
            <Teleport to="body">
              <div
                v-if="coverMenuOpen"
                :style="coverMenuStyle"
                data-cover-menu
                class="fixed z-[9999] w-48 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
              >
                <button
                  @click="centerDetailWorkspaceOnMap"
                  class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 hover:cursor-pointer"
                >
                  <MaximizeIcon class="h-3.5 w-3.5 shrink-0 text-gray-500" />
                  <span>Center on map</span>
                </button>
                <button
                  v-if="isTauriApp"
                  @click="
                    () => {
                      closeCoverMenu()
                      void handleDetailWorkspaceOffline()
                    }
                  "
                  class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 hover:cursor-pointer"
                >
                  <WifiOffIcon class="h-3.5 w-3.5 shrink-0 text-gray-500" />
                  <span>Make available offline</span>
                </button>
                <hr class="my-1 border-gray-100" />
                <button
                  data-testid="pinned-unpin-all"
                  :disabled="!canUnpinAllPinnedItems"
                  @click="handleUnpinAll"
                  class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                  :title="hasItemsToUnpin ? 'Unpin all linked items' : 'No linked items to unpin'"
                >
                  <PinOffIcon class="h-3.5 w-3.5 shrink-0 text-gray-500" />
                  <span>Unpin all</span>
                </button>
              </div>
            </Teleport>
          </div>
          <button
            @click="collapseSidebar"
            data-testid="workspace-sidebar-hide"
            class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 hover:cursor-pointer"
            title="Hide sidebar"
          >
            <PanelTopCloseIcon class="h-[18px] w-[18px]" />
          </button>
        </div>
        <!-- Cinematic cover image area (~210px tall) -->
        <div
          v-else
          class="relative overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300"
          style="height: 210px"
        >
          <!-- Cover image -->
          <img
            v-if="workspaceStore.coverPhotoUrl"
            :src="workspaceStore.coverPhotoUrl"
            alt=""
            class="w-full h-full object-cover pointer-events-none select-none"
            aria-hidden="true"
            draggable="false"
            @contextmenu.prevent
          />
          <!-- Empty state gradient placeholder -->
          <div
            v-else
            class="w-full h-full bg-gradient-to-br from-emerald-50 via-teal-100 to-emerald-200"
          />

          <!-- Remove cover photo button (pick mode only) -->
          <div
            v-if="canEditCollectionCover && isPickCoverMode && workspaceStore.coverPhotoUrl"
            class="absolute inset-0 z-20 flex items-center justify-center bg-black/30"
          >
            <button
              @click="handleRemoveCoverPhoto"
              class="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/90 hover:bg-white text-red-600 hover:text-red-700 text-sm font-medium shadow transition-colors hover:cursor-pointer"
              title="Remove cover photo"
              aria-label="Remove cover photo"
            >
              <XIcon class="w-4 h-4 shrink-0" />
              <span>Remove cover photo</span>
            </button>
          </div>

          <!-- Controls row floating over the image (top) -->
          <div class="absolute inset-x-0 top-0 z-30 flex items-center gap-1 px-2 pt-1.5">
            <button
              v-if="isPickCoverMode"
              @click="handleCancelPickCover"
              class="flex items-center gap-1.5 h-8 px-2 rounded-md bg-black/30 text-white hover:bg-black/50 transition-colors hover:cursor-pointer text-xs font-medium"
              aria-label="Cancel cover photo selection"
              title="Cancel"
            >
              <XIcon class="w-4 h-4 shrink-0" />
              <span>Cancel</span>
            </button>
            <button
              v-if="!isPickCoverMode"
              @click="handleBackToWorkspaces"
              class="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-black/20 text-white transition-colors hover:bg-black/35 hover:cursor-pointer"
              aria-label="Back to collections"
              title="Back to collections"
            >
              <ChevronLeftIcon class="h-5 w-5" />
            </button>
            <div class="flex-1" />
            <button
              @click="collapseSidebar"
              data-testid="workspace-sidebar-hide"
              class="flex items-center justify-center w-8 h-8 rounded-md bg-black/30 text-white hover:bg-black/50 transition-colors hover:cursor-pointer"
              title="Hide sidebar"
            >
              <PanelTopCloseIcon class="h-[18px] w-[18px]" />
            </button>
          </div>

          <!-- Name + meta row pinned to the bottom of the image -->
          <div
            class="absolute inset-x-0 bottom-0 z-10 flex items-end gap-1 px-2 pb-2 bg-gradient-to-t from-black/70 via-black/40 to-transparent"
            style="padding-top: 48px"
          >
            <!-- Name + metadata (left, grows) -->
            <div class="flex-1 min-w-0">
              <!-- Rename input or display -->
              <div class="flex items-center gap-1.5 min-w-0 mb-0.5">
                <div
                  class="rounded px-1 py-0.5 -mx-1 transition-colors min-w-0 shrink"
                  :class="
                    isEditingWorkspaceName
                      ? 'ring-1 ring-white bg-black/40'
                      : canEditCollectionMetadata
                        ? 'hover:ring-1 hover:ring-white/40 cursor-text'
                        : ''
                  "
                  @click="
                    canEditCollectionMetadata && !isEditingWorkspaceName && startRenamingWorkspace()
                  "
                >
                  <input
                    v-if="isEditingWorkspaceName"
                    ref="workspaceNameInputRef"
                    v-model="editedWorkspaceName"
                    class="w-full text-xl font-semibold bg-transparent text-white focus:outline-none"
                    :class="workspaceNameError ? 'ring-1 ring-red-400 rounded' : ''"
                    @keydown.enter.prevent="saveWorkspaceName"
                    @keydown.esc="cancelRenamingWorkspace"
                    @blur="saveWorkspaceName"
                    @click.stop
                  />
                  <span
                    v-else
                    class="text-2xl font-semibold text-white drop-shadow truncate block"
                    >{{ displayedWorkspaceName }}</span
                  >
                </div>
                <template v-if="!isEditingWorkspaceName">
                  <GlobeIcon
                    v-if="isPublic"
                    class="w-3.5 h-3.5 text-white/90 drop-shadow shrink-0"
                    title="Public collection"
                  />
                  <UsersIcon
                    v-else-if="workspaceStore.isCollaborator || workspaceStore.isViewer"
                    class="w-3.5 h-3.5 text-white/90 drop-shadow shrink-0"
                    title="Shared with you"
                  />
                  <span
                    v-if="coverAuthorByline"
                    class="text-sm text-white drop-shadow truncate shrink-0"
                    >by {{ coverAuthorByline }}</span
                  >
                </template>
              </div>
              <p v-if="workspaceNameError" class="text-sm text-red-300 -mt-0.5 mb-0.5">
                {{ workspaceNameError }}
              </p>
              <!-- Stats row (two lines, toggled by click) -->
              <button
                class="flex flex-col gap-1.5 mt-0.5 text-left hover:cursor-pointer focus:outline-none"
                @click.stop="toggleStatsExpanded"
                :title="statsExpanded ? 'Show summary' : 'Show detailed stats'"
              >
                <!-- Primary line: date range -->
                <span class="flex items-center gap-1.5">
                  <span v-if="collectionDateRange" class="text-sm text-white drop-shadow">
                    {{ collectionDateRange }}
                  </span>
                  <ChevronDownIcon
                    class="w-3.5 h-3.5 text-white/90 drop-shadow shrink-0 transition-transform duration-200"
                    :class="statsExpanded ? 'rotate-180' : 'rotate-0'"
                  />
                </span>
                <!-- Secondary line: distance, elevation, duration, avg speed (toggled) -->
                <span v-if="statsExpanded" class="flex items-center gap-4">
                  <span
                    class="flex items-center gap-1"
                    :title="`Total distance: ${(workspaceStore.totalDistance / 1000).toFixed(2)} km`"
                  >
                    <RulerIcon class="w-3 h-3 text-white shrink-0" />
                    <span class="text-sm text-white drop-shadow">{{
                      formatCollectionDistance(workspaceStore.totalDistance)
                    }}</span>
                  </span>
                  <span
                    v-if="visibleCollectionElevationGain !== null"
                    class="flex items-center gap-1"
                    :title="`+${Math.round(visibleCollectionElevationGain)} m elevation gain`"
                  >
                    <ArrowUpRightIcon class="w-3 h-3 text-white shrink-0" />
                    <span class="text-[13px] text-white drop-shadow"
                      >+{{ Math.round(visibleCollectionElevationGain) }}m</span
                    >
                  </span>
                  <span
                    v-if="formatCollectionDuration(workspaceStore.totalTimeElapsed)"
                    class="flex items-center gap-1"
                    :title="
                      exactCollectionDuration(workspaceStore.totalTimeElapsed)
                        ? `Total duration: ${exactCollectionDuration(workspaceStore.totalTimeElapsed)}`
                        : undefined
                    "
                  >
                    <ClockIcon class="w-3 h-3 text-white shrink-0" />
                    <span class="text-[13px] text-white drop-shadow">{{
                      formatCollectionDuration(workspaceStore.totalTimeElapsed)
                    }}</span>
                  </span>
                  <span
                    v-if="workspaceStore.totalAverageSpeed !== null"
                    class="flex items-center gap-1"
                    :title="`${workspaceStore.totalAverageSpeed?.toFixed(2)} km/h average speed`"
                  >
                    <CircleSlash2Icon class="w-3 h-3 text-white shrink-0" />
                    <span class="text-[13px] text-white drop-shadow"
                      >{{ workspaceStore.totalAverageSpeed?.toFixed(1) }} km/h</span
                    >
                  </span>
                </span>
              </button>
            </div>

            <!-- Right-side action buttons -->
            <div class="flex items-center gap-1 shrink-0 pb-0.5">
              <button
                v-if="canBookmark"
                @click="handleToggleBookmark"
                :disabled="isBookmarkBusy"
                class="flex items-center justify-center w-9 h-9 rounded-md bg-black/30 text-white hover:bg-black/50 transition-colors hover:cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                :class="workspaceStore.isBookmarked ? 'text-amber-300' : ''"
                :title="bookmarkButtonTitle"
                :aria-label="bookmarkButtonTitle"
              >
                <BookmarkIcon
                  class="w-5 h-5"
                  :class="workspaceStore.isBookmarked ? 'fill-current' : ''"
                />
              </button>
              <button
                v-if="canShare"
                data-testid="workspace-cover-share-button"
                @click="openShareModal"
                class="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-black/20 text-white transition-colors hover:bg-black/35 hover:cursor-pointer"
                title="Share collection"
                aria-label="Share collection"
              >
                <ShareIcon class="h-5 w-5" />
              </button>

              <!-- Three-dot menu -->
              <div class="relative" data-cover-menu>
                <button
                  @click.stop="toggleCoverMenu"
                  class="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-black/20 text-white transition-colors hover:cursor-pointer hover:bg-black/35"
                  :class="coverMenuOpen ? 'bg-black/40 border-white/45' : ''"
                  title="More options"
                  aria-label="More options"
                >
                  <MoreHorizontalIcon class="w-5 h-5" />
                </button>
                <Teleport to="body">
                  <div
                    v-if="coverMenuOpen"
                    :style="coverMenuStyle"
                    data-cover-menu
                    class="fixed z-[9999] w-48 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden py-1"
                  >
                    <!-- Group 1: Map actions -->
                    <button
                      @click="centerDetailWorkspaceOnMap"
                      class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                    >
                      <MaximizeIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span>Center on map</span>
                    </button>
                    <button
                      :disabled="!hasCurrentWorkspacePinnableItems"
                      @click="handleCurrentWorkspacePinToggle"
                      class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                      :title="
                        hasCurrentWorkspacePinnableItems
                          ? areAllCurrentWorkspaceItemsPinned
                            ? 'Unpin all items'
                            : 'Pin all items'
                          : 'No items to pin'
                      "
                    >
                      <component
                        :is="areAllCurrentWorkspaceItemsPinned ? PinOffIcon : PinIcon"
                        class="w-3.5 h-3.5 text-gray-500 shrink-0"
                      />
                      <span>{{ areAllCurrentWorkspaceItemsPinned ? 'Unpin all' : 'Pin all' }}</span>
                    </button>
                    <hr class="my-1 border-gray-100" />
                    <button
                      v-if="!isPinnedWorkspace"
                      @click="
                        () => {
                          closeCoverMenu()
                          openWorkspaceGpxDownloadModal()
                        }
                      "
                      class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                    >
                      <DownloadIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span>Download GPX</span>
                    </button>
                    <button
                      v-if="!isPinnedWorkspace"
                      @click="
                        () => {
                          closeCoverMenu()
                          toggleStatsExpanded()
                        }
                      "
                      class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                    >
                      <RulerIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span>{{ statsExpanded ? 'Hide stats' : 'Show stats' }}</span>
                    </button>
                    <button
                      v-if="isTauriApp"
                      @click="
                        () => {
                          closeCoverMenu()
                          void handleDetailWorkspaceOffline()
                        }
                      "
                      class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                    >
                      <WifiOffIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span>{{
                        isCurrentWorkspaceOffline ? 'Manage offline data' : 'Make available offline'
                      }}</span>
                    </button>
                    <!-- Group 2: Edit actions -->
                    <hr
                      v-if="
                        !isPinnedWorkspace &&
                        (canEditCollectionCover ||
                          canEditCollectionMetadata ||
                          canEditCollectionDescription)
                      "
                      class="my-1 border-gray-100"
                    />
                    <button
                      v-if="canEditCollectionCover && !isPinnedWorkspace"
                      :disabled="!hasPhotos || isContentLoading"
                      @click="
                        () => {
                          if (!hasPhotos || isContentLoading) return
                          closeCoverMenu()
                          handleChangeCoverPhoto()
                        }
                      "
                      class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      :title="!hasPhotos ? 'No photos in this collection' : 'Change cover photo'"
                    >
                      <ImageIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span>Change photo</span>
                    </button>
                    <button
                      v-if="canEditCollectionMetadata && !isPinnedWorkspace"
                      @click="startRenamingWorkspace"
                      class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                    >
                      <PencilIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span>Rename</span>
                    </button>
                    <button
                      v-if="canEditCollectionDescription && !isPinnedWorkspace"
                      @click="
                        () => {
                          closeCoverMenu()
                          startEditingDescription()
                        }
                      "
                      class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                    >
                      <ScrollTextIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      <span>Edit description</span>
                    </button>
                    <!-- Group 3: Danger zone -->
                    <template v-if="canDeleteWorkspace && !isPinnedWorkspace">
                      <hr class="my-1 border-gray-100" />
                      <button
                        @click="handleDeleteCollection"
                        class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-red-600 hover:bg-red-50 hover:cursor-pointer transition-colors"
                      >
                        <TrashIcon class="w-3.5 h-3.5 shrink-0" />
                        <span>Delete</span>
                      </button>
                    </template>
                    <template v-if="canLeaveWorkspace">
                      <hr class="my-1 border-gray-100" />
                      <button
                        @click="handleLeaveCollection"
                        class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-red-600 hover:bg-red-50 hover:cursor-pointer transition-colors"
                      >
                        <LogOutIcon class="w-3.5 h-3.5 shrink-0" />
                        <span>Leave collection</span>
                      </button>
                    </template>
                  </div>
                </Teleport>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Description field — below cover photo, above action bar -->

    <!-- Collections Overview — WorkspaceSearchPanel manages its own internal scroll, no portal needed -->
    <!-- v-show keeps WorkspaceSearchPanel always mounted to avoid reload on view switch -->
    <Transition name="slide-fade-right" mode="in-out" :duration="500">
      <div v-show="currentView === 'workspaces'" key="collections">
        <WorkspaceSearchPanel
          @select-workspace="handleWorkspaceSelect"
          @make-offline="handleMakeOffline"
        />
      </div>
    </Transition>

    <!-- All Activities & Routes system collection —
         v-show keeps it mounted when navigating away, so isAllActivitiesView stays true and tracks remain rendered -->
    <Transition name="slide-fade-left" mode="out-in" :duration="500">
      <div
        v-show="currentView === 'all-activities'"
        key="all-activities"
        class="flex flex-1 min-h-0 overflow-hidden"
      >
        <ActivityRouteCollection
          ref="activityRouteCollectionRef"
          @back="handleBackToWorkspaces"
          @select-workspace="handleWorkspaceSelect"
          @navigate-to-workspace="handleWorkspaceSelect"
          @zoom-tour="handleZoomTour"
        />
      </div>
    </Transition>

    <!-- Collection Detail View — scroll container starts here so scrollbar is scoped to list only -->
    <Transition
      name="slide-fade-left"
      mode="out-in"
      :duration="500"
      @after-enter="handleWorkspaceDetailAfterEnter"
    >
      <div
        v-if="currentView === 'workspace-detail'"
        key="collection-detail"
        class="relative flex flex-1 min-h-0 flex-col overflow-hidden"
      >
        <div ref="scrollContainer" class="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
          <div class="flex flex-col">
            <div
              v-if="workspaceStore.markedForDeletion"
              class="bg-red-50 border-l-4 border-red-400 p-3"
            >
              <div class="flex items-start gap-3">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-red-800">Collection marked for deletion</p>
                  <p class="text-xs text-red-600 mt-1">
                    Will be permanently deleted in {{ daysUntilDeletion }}
                    {{ daysUntilDeletion === 1 ? 'day' : 'days' }}.
                  </p>
                </div>
                <button
                  v-if="canRestoreWorkspace"
                  @click="handleRestoreCollection"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-white border border-green-200 rounded-md hover:bg-green-50 hover:cursor-pointer transition-colors shrink-0"
                  title="Restore collection"
                  aria-label="Restore collection"
                >
                  <UndoIcon class="w-3.5 h-3.5" />
                  Restore
                </button>
              </div>
            </div>

            <!-- Description field -->
            <div v-if="!isDetailLoading && !isPinnedWorkspace" class="bg-white">
              <div v-if="!isEditingDescription" class="relative p-4">
                <p
                  v-if="linkifyDescription"
                  class="text-sm text-gray-700 whitespace-pre-wrap"
                  :class="descriptionExpanded ? '' : 'line-clamp-6'"
                  v-html="linkifyDescription"
                />
                <p
                  v-else-if="canEditCollectionDescription"
                  class="text-sm text-gray-400 italic hover:cursor-pointer"
                  @click="startEditingDescription"
                >
                  Add a description to this collection...
                </p>
                <button
                  v-if="
                    workspaceStore.description &&
                    !descriptionExpanded &&
                    workspaceStore.description.length > 300
                  "
                  @click.stop="descriptionExpanded = true"
                  class="mt-1 text-xs text-emerald-600 hover:text-emerald-700 hover:cursor-pointer"
                >
                  Show more
                </button>
                <button
                  v-if="
                    workspaceStore.description &&
                    descriptionExpanded &&
                    workspaceStore.description.length > 300
                  "
                  @click.stop="descriptionExpanded = false"
                  class="mt-1 text-xs text-emerald-600 hover:text-emerald-700 hover:cursor-pointer"
                >
                  Show less
                </button>
              </div>
              <div v-else class="p-4">
                <textarea
                  ref="descriptionTextareaRef"
                  v-model="editedDescription"
                  class="w-full text-sm border rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500 overflow-hidden"
                  :class="descriptionError ? 'border-red-300' : 'border-gray-300'"
                  rows="3"
                  placeholder="Add a description to this collection..."
                  @input="
                    (e) => {
                      const el = e.target as HTMLTextAreaElement
                      el.style.height = 'auto'
                      el.style.height = el.scrollHeight + 'px'
                    }
                  "
                  @keydown.enter.exact.prevent="saveDescription"
                  @keydown.shift.enter.stop
                  @keydown.esc="cancelEditingDescription"
                  @blur="saveDescription"
                />
                <p v-if="descriptionError" class="text-xs text-red-600 mt-1">
                  {{ descriptionError }}
                </p>
              </div>
            </div>

            <!-- Content Section -->
            <div class="relative">
              <!-- Loading State (delayed bike) -->
              <div v-if="isDetailLoading" class="p-4 text-center text-gray-500">
                <div class="relative w-full h-8 mb-2 overflow-hidden">
                  <BikeIcon
                    v-if="showLoadingIcon"
                    class="w-8 h-8 text-primary-500 animate-bike-ride"
                  />
                </div>
                <p v-if="showLoadingIcon" class="text-sm">Workspace loading...</p>
              </div>

              <template v-else>
                <div
                  v-if="
                    isPinnedWorkspace && isPinnedWorkspaceEmpty && isPinnedWorkspaceDisplayReady
                  "
                  class="bg-white p-4"
                >
                  <PinnedEmptyState
                    @go-library="navigatePinnedEmptyStateToLibrary"
                    @go-collections="navigatePinnedEmptyStateToCollections"
                  />
                </div>

                <div ref="toursSectionAnchorRef" class="h-0" />
                <!-- Tours Header -->
                <BaseSectionHeader
                  :ref="setToursHeaderEl"
                  title="Activities & Routes"
                  :count="workspaceStore.tourFileCount"
                  clickable
                  @header-click="requestSectionScroll('tours')"
                >
                  <template #leading>
                    <button
                      @click.stop="tourLockListToMap = !tourLockListToMap"
                      class="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md transition hover:cursor-pointer hover:bg-gray-100 shrink-0"
                      title="Sync list to visible map area"
                      aria-label="Sync list to map"
                    >
                      <BaseToggle :model-value="tourLockListToMap" />
                      <span
                        class="text-[11px] font-medium shrink-0"
                        :class="tourLockListToMap ? 'text-emerald-600' : 'text-gray-400'"
                        >Sync to map</span
                      >
                    </button>
                  </template>
                  <template #actions>
                    <button
                      class="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 hover:cursor-pointer rounded-md transition disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-default"
                      :title="isAnyTourVisible ? 'Hide all tours' : 'Show all tours'"
                      :aria-label="isAnyTourVisible ? 'Hide all tours' : 'Show all tours'"
                      :disabled="!hasTours"
                      @click.stop="handleToggleAllToursVisibility"
                    >
                      <component :is="isAnyTourVisible ? EyeIcon : EyeOffIcon" class="w-4 h-4" />
                    </button>
                    <div class="relative shrink-0" data-tour-sort-menu>
                      <button
                        @click.stop="toggleTourSortMenu"
                        class="w-7 h-7 flex items-center justify-center rounded-md transition hover:cursor-pointer"
                        :class="
                          tourSortMenuOpen
                            ? 'text-emerald-600 bg-emerald-100'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                        "
                        title="Sort tours"
                        aria-label="Sort tours"
                      >
                        <ArrowUpDownIcon class="w-4 h-4 shrink-0" />
                      </button>
                      <Teleport to="body">
                        <div
                          v-if="tourSortMenuOpen"
                          :style="tourSortMenuStyle"
                          data-tour-sort-menu
                          class="fixed z-[9999] w-56 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden py-1"
                        >
                          <button
                            v-for="opt in tourSortOptions"
                            :key="`${opt.sortBy}-${opt.sortDir}`"
                            @click="setTourSort(opt)"
                            class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 hover:cursor-pointer transition-colors"
                            :class="
                              isActiveTourSort(opt)
                                ? 'text-emerald-700 bg-emerald-50 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            "
                          >
                            <CheckIcon
                              v-if="isActiveTourSort(opt)"
                              class="w-3.5 h-3.5 text-emerald-600 shrink-0"
                            />
                            <span v-else class="w-3.5 h-3.5 shrink-0" />
                            {{ opt.label }}
                          </button>
                        </div>
                      </Teleport>
                    </div>
                  </template>
                </BaseSectionHeader>

                <div>
                  <TourList
                    v-if="hasTours"
                    :override-tours="detailDisplayTours"
                    @zoom-tour="handleZoomTour"
                    @select-tour="handleSelectTour"
                    @enter-track-edit="handleEnterTrackEdit"
                    @navigate-to-workspace="handleWorkspaceSelect"
                  />
                  <div
                    v-else-if="!isPinnedWorkspace || isPinnedWorkspaceEmpty"
                    :class="[
                      'p-4',
                      isPinnedWorkspace && isPinnedWorkspaceDisplayReady
                        ? 'bg-emerald-50/70'
                        : 'bg-white',
                    ]"
                  >
                    <p
                      v-if="isPinnedWorkspace && isPinnedWorkspaceDisplayReady"
                      class="text-sm text-gray-500"
                    >
                      No pinned activities or routes yet.
                    </p>
                    <div
                      v-if="
                        isPinnedWorkspace &&
                        isPinnedWorkspaceEmpty &&
                        isPinnedWorkspaceDisplayReady &&
                        canAddCollectionContent
                      "
                      class="mt-3 flex flex-wrap gap-2"
                    >
                      <button
                        type="button"
                        aria-label="Draw route"
                        :disabled="isContentLoading"
                        class="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:cursor-pointer hover:bg-emerald-100/70 disabled:cursor-not-allowed disabled:opacity-50"
                        @click.stop="handleDrawRouteIntoCollection"
                      >
                        <RouteIcon class="h-3.5 w-3.5 text-emerald-700" />
                        <span>Draw route</span>
                      </button>
                      <button
                        type="button"
                        aria-label="Import"
                        :disabled="isContentLoading"
                        class="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:cursor-pointer hover:bg-emerald-100/70 disabled:cursor-not-allowed disabled:opacity-50"
                        @click.stop="triggerGpxUpload"
                      >
                        <UploadIcon class="h-3.5 w-3.5 text-emerald-700" />
                        <span>Import</span>
                      </button>
                    </div>
                    <CollectionRouteEmptyState
                      v-else-if="canAddCollectionContent && isPinnedWorkspaceContentReady"
                      :busy="isContentLoading"
                      @import-gpx="triggerGpxUpload"
                      @import-photo="triggerPhotoUpload"
                      @draw-route="handleDrawRouteIntoCollection"
                    />
                    <p v-else-if="isPinnedWorkspaceContentReady" class="text-sm text-gray-500">
                      No activities or routes in this collection.
                    </p>
                  </div>
                  <div
                    v-if="hasTours && tourLockListToMap && totalTourCount - filteredTourCount > 0"
                    class="bg-white border-t border-gray-200 py-2.5 px-3"
                  >
                    <span class="block text-[11px] text-gray-400" style="padding-left: 22px">
                      {{ totalTourCount - filteredTourCount }}
                      {{
                        totalTourCount - filteredTourCount === 1
                          ? "item out of view isn't listed"
                          : "items out of view aren't listed"
                      }}
                    </span>
                  </div>
                </div>

                <div ref="poiSectionAnchorRef" class="h-0" />
                <!-- POIs Header -->
                <BaseSectionHeader
                  :ref="setPoiHeaderEl"
                  title="Points of Interest"
                  :count="workspaceStore.allWorkspacePOIs.length"
                  clickable
                  @header-click="requestSectionScroll('poi')"
                >
                  <template #actions>
                    <BaseIconButton
                      v-if="canAddPOIToCollection && !isPinnedWorkspace"
                      size="sm"
                      title="Add POI"
                      aria-label="Add POI"
                      @click.stop="handleOpenAddPOIModal"
                    >
                      <PlusIcon class="h-4 w-4" />
                    </BaseIconButton>
                    <BaseIconButton
                      size="sm"
                      :title="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                      :aria-label="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                      @click.stop="handleTogglePOIsVisibility"
                    >
                      <EyeIcon v-if="uiStore.arePOIsVisible" class="h-4 w-4" />
                      <EyeOffIcon v-else class="h-4 w-4" />
                    </BaseIconButton>
                  </template>
                </BaseSectionHeader>

                <div v-if="hasPOIs" class="bg-white">
                  <WorkspacePOIList
                    :selected-poi-id="selectedPoiId ?? null"
                    @zoom-poi="handleZoomPOI"
                    @open-poi-detail="handleOpenPOIDetail"
                  />
                </div>
                <div
                  v-else-if="!isPinnedWorkspace || isPinnedWorkspaceEmpty"
                  :class="[
                    'p-4',
                    isPinnedWorkspace && isPinnedWorkspaceDisplayReady
                      ? 'bg-emerald-50/70'
                      : 'bg-white',
                  ]"
                >
                  <p class="text-sm text-gray-500">No points of interest in this collection.</p>
                  <div
                    v-if="
                      isPinnedWorkspace && isPinnedWorkspaceEmpty && isPinnedWorkspaceDisplayReady
                    "
                    class="mt-3 flex flex-wrap gap-2"
                  >
                    <button
                      v-if="canAddPOIToCollection"
                      type="button"
                      aria-label="Mark POI"
                      class="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:cursor-pointer hover:bg-emerald-100/70"
                      @click.stop="handleOpenAddPOIModal"
                    >
                      <MapPinIcon class="h-3.5 w-3.5 text-emerald-700" />
                      <span>Mark POI</span>
                    </button>
                    <button
                      v-if="canAddCollectionContent"
                      type="button"
                      aria-label="Import POIs"
                      :disabled="isContentLoading"
                      class="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:cursor-pointer hover:bg-emerald-100/70 disabled:cursor-not-allowed disabled:opacity-50"
                      @click.stop="triggerGpxUpload"
                    >
                      <UploadIcon class="h-3.5 w-3.5 text-emerald-700" />
                      <span>Import</span>
                    </button>
                  </div>
                </div>

                <div>
                  <div ref="photosSectionAnchorRef" class="h-0" />
                  <!-- Photos Header -->
                  <div
                    :class="[
                      'px-3',
                      'bg-white',
                      'hover:cursor-pointer',
                      'hover:bg-gray-200',
                      'transition-colors',
                    ]"
                    ref="photosHeaderRef"
                    @click="requestSectionScroll('photos')"
                  >
                    <div class="flex items-center justify-between h-[38px]">
                      <div class="flex items-center gap-1.5 min-w-0">
                        <h3 class="text-[16px] font-semibold text-gray-900">
                          Photos ({{ workspaceStore.photoFileCount }})
                        </h3>
                        <span
                          v-if="isPhotoFiltering"
                          class="text-[11px] text-gray-400 shrink-0 ml-0.5"
                        >
                          {{ `${filteredPhotoCount}/${totalPhotoCount} listed` }}
                        </span>
                      </div>
                      <button
                        class="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 hover:cursor-pointer rounded-md transition disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-default"
                        :title="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                        :aria-label="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                        :disabled="!hasPhotos"
                        @click.stop="handleTogglePhotosVisibility"
                      >
                        <component :is="arePhotosVisible ? EyeIcon : EyeOffIcon" class="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="hasPhotos && isPickCoverMode"
                    class="flex items-center gap-2 px-3 py-2 bg-emerald-50 border-b border-emerald-200"
                  >
                    <ImageIcon class="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span class="flex-1 text-xs text-emerald-700 font-medium"
                      >Tap a photo to set as collection cover</span
                    >
                    <button
                      @click="handleCancelPickCover"
                      class="flex items-center justify-center w-5 h-5 rounded text-emerald-600 hover:bg-emerald-100 hover:cursor-pointer transition-colors"
                      aria-label="Cancel pick cover"
                    >
                      <XIcon class="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div v-if="hasPhotos" class="border-b border-gray-100">
                    <PhotoList
                      :dimmed="!arePhotosVisible"
                      :override-photos="filteredPhotoList"
                      :pick-cover-mode="isPickCoverMode"
                      :can-delete-photos="canDeletePhotos"
                      :can-set-cover-photo="canEditCollectionCover"
                      :animate-pinned-removal="isPinnedWorkspace"
                      @view-photo="
                        isPickCoverMode ? handlePickCoverPhoto($event) : handleViewPhoto($event)
                      "
                      @zoom-photo="handlePhotoZoom"
                    />
                  </div>
                  <div
                    v-else-if="!isPinnedWorkspace || isPinnedWorkspaceEmpty"
                    class="bg-white border-b border-gray-100 p-4"
                  >
                    <p class="text-sm text-gray-500">No photos in this collection.</p>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div
          v-if="!isDetailLoading && (showToursTopHint || showPOITopHint || showPhotosTopHint)"
          class="absolute inset-x-0 top-0 z-40 flex flex-col border-b border-gray-200"
        >
          <div
            v-if="showToursTopHint"
            data-testid="tours-top-hint"
            class="pointer-events-auto bg-white px-3 hover:bg-gray-200 hover:cursor-pointer transition-colors"
            @pointerdown.stop.prevent="requestSectionScroll('tours')"
            @click="requestSectionScroll('tours')"
          >
            <div class="flex items-center justify-between h-[38px]">
              <div class="flex items-center gap-1.5 min-w-0">
                <h3 class="text-[16px] font-semibold text-gray-900 shrink-0">
                  Activities &amp; Routes ({{ workspaceStore.tourFileCount }})
                </h3>
                <button
                  @click.stop="tourLockListToMap = !tourLockListToMap"
                  class="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md transition hover:cursor-pointer hover:bg-gray-100 shrink-0"
                  title="Sync list to visible map area"
                  aria-label="Sync list to map"
                >
                  <BaseToggle :model-value="tourLockListToMap" />
                  <span
                    class="text-[11px] font-medium shrink-0"
                    :class="tourLockListToMap ? 'text-emerald-600' : 'text-gray-400'"
                    >Sync to map</span
                  >
                </button>
              </div>
              <div class="flex items-center gap-0.5 shrink-0 pr-5">
                <button
                  class="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 hover:cursor-pointer rounded-md transition disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-default"
                  :title="isAnyTourVisible ? 'Hide all tours' : 'Show all tours'"
                  :aria-label="isAnyTourVisible ? 'Hide all tours' : 'Show all tours'"
                  :disabled="!hasTours"
                  @click.stop="handleToggleAllToursVisibility"
                >
                  <component :is="isAnyTourVisible ? EyeIcon : EyeOffIcon" class="w-4 h-4" />
                </button>
                <div class="relative shrink-0" data-tour-sort-menu>
                  <button
                    @click.stop="toggleTourSortMenu"
                    class="w-7 h-7 flex items-center justify-center rounded-md transition hover:cursor-pointer"
                    :class="
                      tourSortMenuOpen
                        ? 'text-emerald-600 bg-emerald-100'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                    "
                    title="Sort tours"
                    aria-label="Sort tours"
                  >
                    <ArrowUpDownIcon class="w-4 h-4 shrink-0" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="showPOITopHint"
            data-testid="poi-top-hint"
            class="pointer-events-auto border-t border-gray-200 bg-white px-3 hover:bg-gray-200 hover:cursor-pointer transition-colors"
            @pointerdown.stop.prevent="requestSectionScroll('poi')"
            @click="requestSectionScroll('poi')"
          >
            <div class="flex items-center justify-between h-[38px]">
              <div class="flex items-center gap-1.5 min-w-0">
                <h3 class="text-[16px] font-semibold text-gray-900">
                  Points of Interest ({{ workspaceStore.allWorkspacePOIs.length }})
                </h3>
              </div>
              <div class="flex items-center gap-1 pr-5">
                <button
                  v-if="canAddPOIToCollection"
                  type="button"
                  class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 hover:cursor-pointer"
                  title="Add POI"
                  aria-label="Add POI"
                  @click.stop="handleOpenAddPOIModal"
                >
                  <PlusIcon class="h-4 w-4" />
                </button>
                <button
                  type="button"
                  class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 hover:cursor-pointer"
                  :title="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                  :aria-label="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                  @click.stop="handleTogglePOIsVisibility"
                >
                  <EyeIcon v-if="uiStore.arePOIsVisible" class="h-4 w-4" />
                  <EyeOffIcon v-else class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="showPhotosTopHint"
            data-testid="photos-top-hint"
            class="pointer-events-auto border-t border-gray-200 bg-white px-3 hover:bg-gray-200 hover:cursor-pointer transition-colors"
            @pointerdown.stop.prevent="requestSectionScroll('photos')"
            @click="requestSectionScroll('photos')"
          >
            <div class="flex items-center justify-between h-[38px]">
              <div class="flex items-center gap-1.5 min-w-0">
                <h3 class="text-[16px] font-semibold text-gray-900">
                  Photos ({{ workspaceStore.photoFileCount }})
                </h3>
                <span v-if="isPhotoFiltering" class="text-[11px] text-gray-400 shrink-0 ml-0.5">
                  {{ `${filteredPhotoCount}/${totalPhotoCount} listed` }}
                </span>
              </div>
              <div class="pr-5">
                <button
                  class="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 hover:cursor-pointer rounded-md transition disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-default"
                  :title="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                  :aria-label="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                  :disabled="!hasPhotos"
                  @click.stop="handleTogglePhotosVisibility"
                >
                  <component :is="arePhotosVisible ? EyeIcon : EyeOffIcon" class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            !isDetailLoading && (showToursBottomHint || showPOIBottomHint || showPhotosBottomHint)
          "
          class="absolute inset-x-0 bottom-0 z-40 flex flex-col"
        >
          <div
            v-if="showToursBottomHint"
            data-testid="tours-bottom-hint"
            class="pointer-events-auto border-t border-gray-100 bg-white px-3 hover:bg-gray-200 hover:cursor-pointer transition-colors"
            @pointerdown.stop.prevent="requestSectionScroll('tours')"
            @click="requestSectionScroll('tours')"
          >
            <div class="flex items-center justify-between h-[38px]">
              <div class="flex items-center gap-1.5 min-w-0">
                <h3 class="text-[16px] font-semibold text-gray-900 shrink-0">
                  Activities &amp; Routes ({{ workspaceStore.tourFileCount }})
                </h3>
                <button
                  @click.stop="tourLockListToMap = !tourLockListToMap"
                  class="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md transition hover:cursor-pointer hover:bg-gray-100 shrink-0"
                  title="Sync list to visible map area"
                  aria-label="Sync list to map"
                >
                  <BaseToggle :model-value="tourLockListToMap" />
                  <span
                    class="text-[11px] font-medium shrink-0"
                    :class="tourLockListToMap ? 'text-emerald-600' : 'text-gray-400'"
                    >Sync to map</span
                  >
                </button>
              </div>
              <div class="flex items-center gap-0.5 shrink-0 pr-5">
                <button
                  class="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 hover:cursor-pointer rounded-md transition disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-default"
                  :title="isAnyTourVisible ? 'Hide all tours' : 'Show all tours'"
                  :aria-label="isAnyTourVisible ? 'Hide all tours' : 'Show all tours'"
                  :disabled="!hasTours"
                  @click.stop="handleToggleAllToursVisibility"
                >
                  <component :is="isAnyTourVisible ? EyeIcon : EyeOffIcon" class="w-4 h-4" />
                </button>
                <div class="relative shrink-0" data-tour-sort-menu>
                  <button
                    @click.stop="toggleTourSortMenu"
                    class="w-7 h-7 flex items-center justify-center rounded-md transition hover:cursor-pointer"
                    :class="
                      tourSortMenuOpen
                        ? 'text-emerald-600 bg-emerald-100'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                    "
                    title="Sort tours"
                    aria-label="Sort tours"
                  >
                    <ArrowUpDownIcon class="w-4 h-4 shrink-0" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="showPOIBottomHint"
            data-testid="poi-bottom-hint"
            class="pointer-events-auto border-t border-gray-200 bg-white px-3 hover:bg-gray-200 hover:cursor-pointer transition-colors"
            @pointerdown.stop.prevent="requestSectionScroll('poi')"
            @click="requestSectionScroll('poi')"
          >
            <div class="flex items-center justify-between h-[38px]">
              <div class="flex items-center gap-1.5 min-w-0">
                <h3 class="text-[16px] font-semibold text-gray-900">
                  Points of Interest ({{ workspaceStore.allWorkspacePOIs.length }})
                </h3>
              </div>
              <div class="flex items-center gap-1 pr-5">
                <button
                  v-if="canAddPOIToCollection"
                  type="button"
                  class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 hover:cursor-pointer"
                  title="Add POI"
                  aria-label="Add POI"
                  @click.stop="handleOpenAddPOIModal"
                >
                  <PlusIcon class="h-4 w-4" />
                </button>
                <button
                  type="button"
                  class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 hover:cursor-pointer"
                  :title="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                  :aria-label="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                  @click.stop="handleTogglePOIsVisibility"
                >
                  <EyeIcon v-if="uiStore.arePOIsVisible" class="h-4 w-4" />
                  <EyeOffIcon v-else class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="showPhotosBottomHint"
            data-testid="photos-bottom-hint"
            class="pointer-events-auto border-t border-gray-200 bg-white px-3 hover:bg-gray-200 hover:cursor-pointer transition-colors"
            @pointerdown.stop.prevent="requestSectionScroll('photos')"
            @click="requestSectionScroll('photos')"
          >
            <div class="flex items-center justify-between h-[38px]">
              <div class="flex items-center gap-1.5 min-w-0">
                <h3 class="text-[16px] font-semibold text-gray-900">
                  Photos ({{ workspaceStore.photoFileCount }})
                </h3>
                <span v-if="isPhotoFiltering" class="text-[11px] text-gray-400 shrink-0 ml-0.5">
                  {{ `${filteredPhotoCount}/${totalPhotoCount} listed` }}
                </span>
              </div>
              <div class="pr-5">
                <button
                  class="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 hover:cursor-pointer rounded-md transition disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-default"
                  :title="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                  :aria-label="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                  :disabled="!hasPhotos"
                  @click.stop="handleTogglePhotosVisibility"
                >
                  <component :is="arePhotosVisible ? EyeIcon : EyeOffIcon" class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Info popover for sync-to-map note -->
    <Teleport to="body">
      <div
        v-if="infoPopoverOpen"
        :style="infoPopoverStyle"
        data-detail-info-popover
        class="fixed z-[9999] w-60 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-[11px] text-gray-600 leading-relaxed"
      >
        The list is synced to what's visible on the map. Unsync the list from the map in the filter
        settings to see all tours and photos.
      </div>
    </Teleport>

    <!-- Share Modal -->
    <Teleport to="body">
      <ShareModal :is-open="isShareModalOpen" @close="closeShareModal" />
    </Teleport>

    <Teleport to="body">
      <CreateTourModal
        :is-open="isCreateTourModalOpen"
        mode="route"
        @close="isCreateTourModalOpen = false"
        @create="handleCreateRouteFromModal($event)"
      />
    </Teleport>

    <Teleport to="body">
      <GpxDownloadOptionsModal
        :is-open="isWorkspaceGpxDownloadModalOpen"
        title="Download Collection GPX"
        download-label="Download Collection GPX"
        :is-downloading="isWorkspaceGpxDownloading"
        @close="closeWorkspaceGpxDownloadModal"
        @download="handleWorkspaceGpxDownload"
      />
    </Teleport>

    <!-- (resize handle removed) -->
  </div>
</template>

<style scoped>
@keyframes bike-ride {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(calc(100vw - 2rem));
  }
}

.animate-bike-ride {
  animation: bike-ride 4s ease-in-out infinite;
}

/* Animation: Collections to Collection Detail (fade out right, fade in left) */
.slide-fade-left-enter-active,
.slide-fade-left-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-fade-left-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-fade-left-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* Animation: Collection Detail to Collections (fade out left, fade in right) */
.slide-fade-right-enter-active,
.slide-fade-right-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-fade-right-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-fade-right-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}
</style>
